/**
 * Plugin Execution Store — Zustand store for per-definition execution state.
 *
 * This store is a thin client that delegates all execution to the Engine Server
 * via the platform's engine service. Execution state updates come from
 * two sources:
 *   1. Async execution jobs (execution/start + execution/getState)
 *   2. Engine Server notifications — for output, progress, cascade, and document changes
 *
 * The store preserves the same external API shape as the original in-process version
 * so UI components do not need changes.
 *
 * Platform abstraction:
 *   UI Component -> store.runAction() -> platform.engine.executionStart() -> Engine Server
 *   Engine Server -> notifications -> store._handleNotification() -> Zustand state update
 */

import type {
    ContentDefinition,
    ExecutionUnitOutput,
    ForBlock,
    ImportBinding,
    PluginDefinition,
    PluginOutput,
} from "@magic-md/core";
import {
    ErrorCodes,
    type ClientConfigOverlay,
    type DocumentChangedEvent,
    type PluginInfo,
    type SessionGetStateResult,
    type SessionSyncResult,
} from "@magic-md/engine-client/protocol";
import type { ActionResult, ApiResult } from "@magic-md/plugin-sdk";
import { t as translate } from "@magic-md/i18n";
import { create } from "zustand";
import type { EngineNotification, EngineService } from "../platform";
import { getPlatformService } from "../platform";
import { getImportCachedSymbolOutput } from "../lib/import-cache-output";
import { getDocumentBundleDir, getDocumentPathFromAssetsPath } from "../lib/publish-utils";
import { frontendLog } from "../utils/frontend-logger";
import {
    getActiveTabId,
    setActiveTabIdProvider,
} from "./active-tab-context";
export {
    getActiveTabId,
    setActiveTabIdProvider,
} from "./active-tab-context";
import type { ApplyHydratedUnitOptions } from "./execution-entry-builders";
import { ClientExecutionConfigCache } from "./client-execution-config-cache";
import {
    collectBackgroundRunningTabs,
    equalStringSets,
} from "./background-running-tabs";
import { buildEngineUnitMaps } from "./engine-unit-projection";
import {
    clearChangedUnitExecutions,
    createHydratedExecutionMaps,
    createHydratedUnitOutputPatch,
    executionMapRefsChanged,
    pruneStaleImportedNamespaceExecutions,
    reuseMapIfJsonEntriesUnchanged,
    withoutChangedOutputs,
} from "./execution-hydration";
export { cloneExecutionMaps } from "./execution-hydration";
import { EngineInitController } from "./engine-init-controller";
import {
    activateSessionExecutionSnapshot,
    createEmptySessionExecutionSnapshot,
    getOrCreateSessionExecutionSnapshot,
    snapshotActiveExecutionProjection,
    type SessionExecutionSnapshot,
} from "./execution-session-projection";
import {
    EngineOperationController,
    type EngineOperationLease,
    isStaleEngineOperationError,
} from "./engine-operation-controller";
import {
    ExecutionJobWaiter,
    waitForEnginePromise,
} from "./execution-job-waiter";
import type {
    ContentExecutionEntry,
    ContentExecutionStatus,
    ExecutionEntry,
    ForExecutionEntry,
    ForExecutionStatus,
    ImportExecutionEntry,
    ImportExecutionStatus,
    RunAllProgress,
    UseExecutionEntry,
    UseExecutionStatus,
} from "./execution-state";
import { useEditorStore, isTabMigrating, type EditorTab } from "./editor-store";
import { setEngineInitializer } from "./engine-initializer";
import { resolveEngineDisplayContent } from "./engine-display-content";
import { resetEngineOverviewStore } from "./engine-overview-store";
import {
    getImportedRenderedNodeResult,
    isImportedRenderedUnitStillRunning,
    markImportedSourceRenderedUnitError,
    markImportedSourceRenderedUnitRunning,
    type RunImportedSourceUnitOptions,
} from "./imported-source-execution-state";
export type {
    ImportedSourceUnitKind,
    RunImportedSourceUnitOptions,
} from "./imported-source-execution-state";
import { useLogStore } from "./log-store";
import { getNotificationBus, resetNotificationBus } from "./notification-bus";
import { registerNotificationHandlers } from "./notification-handlers";
import { createParsedOutputExecutionPatch } from "./parsed-output-execution-restore";
import { useSettingsStore } from "./settings-store";
import { sessionRevisionTracker } from "./session-revision-tracker";
import { tabSessionRegistry } from "./tab-session-registry";
import { TabSessionCreationController } from "./tab-session-creation-controller";
import { TabSessionSyncTracker } from "./tab-session-sync-tracker";
import { pinnedUnitSessionRegistry } from "./pinned-unit-sessions";

const ENGINE_SWITCHED_DURING_EXECUTION_MESSAGE = "Engine switched during execution";

const lazyOutputHydrationPromises = new Map<string, Promise<void>>();
const engineOperationController = new EngineOperationController();
const clientExecutionConfigCache = new ClientExecutionConfigCache();
const executionJobWaiter = new ExecutionJobWaiter({
    getEngine: () => getPlatformService().engine,
    getNotificationBus,
});

const DOCUMENT_IMPORT_DIRECTIVE_RE =
    /(^|\n)\s*::import\{[^}\n]*\bdocument\s*=\s*["']?true["']?[^}\n]*\}/;
const CIRCULAR_DEPENDENCY_TEXT = String.fromCharCode(24490, 29615, 20381, 36182);

function circularDependencyMessage(): string {
    return translate("editor", "execution.circularDependency");
}

export function shouldHydrateSessionOutputsForContent(content: string | undefined): boolean {
    if (!content) return false;
    return DOCUMENT_IMPORT_DIRECTIVE_RE.test(content);
}

async function loadClientExecutionConfig(
    lease?: EngineOperationLease,
): Promise<ClientConfigOverlay | undefined> {
    return clientExecutionConfigCache.load({
        liveSnapshot: useSettingsStore.getState().snapshot,
        loadSnapshot: () =>
            getPlatformService().settings.getSnapshot({ owner: "plugin" }),
        assertCurrent: lease ? () => lease.assertCurrent() : undefined,
    });
}

export type { SessionExecutionSnapshot } from "./execution-session-projection";
export type {
    ContentExecutionEntry,
    ContentExecutionStatus,
    ExecutionEntry,
    ExecutionStatus,
    ForExecutionEntry,
    ForExecutionStatus,
    ForIterationEntry,
    ImportExecutionEntry,
    ImportExecutionStatus,
    RunAllProgress,
    UseExecutionEntry,
    UseExecutionStatus,
} from "./execution-state";
export {
    cloneExecutionMapForUnitType,
    getOrCreateSessionExecutionSnapshot as getOrCreateSnapshot,
} from "./execution-session-projection";

const createEmptySnapshot = createEmptySessionExecutionSnapshot;
const getOrCreateSnapshot = getOrCreateSessionExecutionSnapshot;

/**
 * Save the current "active" fields from the store into the session cache.
 * Called on tab switch (save outgoing tab) and after background _ensureSession.
 */
function saveActiveToSnapshot(
    store: PluginExecutionState,
): SessionExecutionSnapshot {
    return snapshotActiveExecutionProjection(store);
}

/**
 * Build a Zustand state patch that restores active fields from a snapshot.
 */
function snapshotToActiveState(snapshot: SessionExecutionSnapshot): Partial<PluginExecutionState> {
    return activateSessionExecutionSnapshot(snapshot);
}

/**
 * Count how many tabs are bound to a given sessionId in the registry.
 * (Multiple tabs can share a session — e.g. the same file open in two
 * panes.)
 */
function countTabsForSession(sessionId: string): number {
    let count = 0;
    for (const link of tabSessionRegistry.entries()) {
        if (link.sessionId === sessionId) count++;
    }
    return count;
}

export interface PluginExecutionState {
    /** Per-definition execution state (active tab) */
    executions: Map<string, ExecutionEntry>;

    /** Whether a "Run All" batch is in progress (active tab) */
    isRunningAll: boolean;

    /** Progress of the current "Run All" batch (active tab) */
    runAllProgress: RunAllProgress | null;

    /** Current async execution job for Run All, if any. */
    activeRunAllExecutionId: string | null;

    /** Current async execution job per unit ID. */
    activeExecutionIdsByUnit: Map<string, string>;

    /** Current Engine Server session ID (active tab, null = not yet connected) */
    sessionId: string | null;

    /** Global execution error (e.g. circular dependency) */
    executionError: string | null;

    /** Whether the Engine Server has been initialized */
    engineReady: boolean;

    /** Plugin info from Engine Server (for primary action lookup) */
    enginePlugins: PluginInfo[];

    /**
     * Engine-resolved definitions (includes imported ns/id symbols).
     * Populated from session create/sync response `units`.
     * Keyed by definition ID (including qualified ns/id).
     */
    engineDefinitions: Map<string, PluginDefinition>;

    /**
     * Engine-resolved content definitions (includes imported ns/id content symbols).
     * Populated from session create/sync response `units`.
     * Keyed by content definition ID (including qualified ns/id).
     */
    engineContentDefinitions: Map<string, ContentDefinition>;

    /**
     * Engine-resolved for blocks (includes materialized ::use for templates).
     * Keyed by for/render ID.
     */
    engineForBlocks: Map<string, ForBlock>;

    /** Set of tab IDs that have running executions in the background */
    backgroundRunningTabs: Set<string>;

    /** Tabs whose Run All request is starting before a session/job is available. */
    runAllPendingTabIds: Set<string>;

    /* ── Per-tab / per-session reactive state ── */

    /**
     * Map tabId → sessionId — sessions survive tab switches.
     *
     * **Derived** snapshot of {@link tabSessionRegistry}.  Do not mutate
     * directly; call `tabSessionRegistry.link / unlinkTab / unlinkSession /
     * clear` instead — the registry's subscribe callback (registered at
     * module init) republishes this field whenever it changes.  Kept on the
     * Zustand state so existing reactive selectors continue to fire.
     */
    tabSessionMap: Map<string, string>;

    /**
     * Session-keyed execution snapshot cache.
     * Each engine session's full execution state is cached here.
     * On tab switch, the UI reads from the snapshot for the new tab's session.
     * Events from background sessions update the cache directly.
     */
    sessionExecutionCache: Map<string, SessionExecutionSnapshot>;

    /**
     * Monotonic per-tab sync revision.
     * Bumped only when the Engine-accepted structural document content changes,
     * allowing intelligence features to refresh after a real sync instead of
     * on every local keystroke.
     */
    tabSyncVersionMap: Map<string, number>;

    /** Set of visible tab IDs (for multi-pane: > 1 when panes show different tabs) */
    visibleTabIds: Set<string>;

    /* ── For-loop execution state ── */

    /** Per-for-block execution state (active tab) */
    forExecutions: Map<string, ForExecutionEntry>;

    /** Per-content-block execution state (active tab) */
    contentExecutions: Map<string, ContentExecutionEntry>;

    /** Per-import-block execution state (active tab) */
    importExecutions: Map<string, ImportExecutionEntry>;

    /** Per-use-block execution state (active tab) */
    useExecutions: Map<string, UseExecutionEntry>;

    /** Get for-execution entry for a for block */
    getForExecution: (forId: string) => ForExecutionEntry;

    /**
     * Run a for block.
     * Delegates to Engine Server's async execution job API with the for block ID.
     */
    runForAction: (forId: string) => Promise<void>;

    /**
     * Run a content block.
     * Delegates to Engine Server's async execution job API with the content block ID.
     */
    runContentAction: (contentId: string) => Promise<void>;

    /**
     * Run an import block.
     * Delegates to Engine Server's async execution job API with the import namespace as unitId.
     */
    runImportAction: (importId: string) => Promise<void>;

    /**
     * Run the source unit behind a non-use imported render, then refresh the
     * current document's import snapshot.
     */
    runImportedSourceAction: (options: RunImportedSourceUnitOptions) => Promise<ActionResult>;

    /**
     * Run a use block.
     * Delegates to Engine Server's async execution job API with the use block ID.
     */
    runUseAction: (useId: string) => Promise<void>;

    /** Get execution entry for a definition */
    getExecution: (id: string) => ExecutionEntry;

    /** Get output for a definition (from last successful execution) */
    getOutput: (id: string) => PluginOutput | null;

    /** Hydrate one persisted output from the active engine session on demand. */
    hydrateOutput: (unitId: string) => Promise<void>;

    /**
     * Run a specific action on a definition.
     *
     * Delegates to Engine Server's async execution job API.
     * State updates come from both the RPC response and engine notifications.
     */
    runAction: (
        definition: PluginDefinition,
        actionName: string,
        _definitions: Map<string, PluginDefinition>,
        _options?: { actionParams?: Record<string, unknown> },
    ) => Promise<ActionResult>;

    /**
     * Call a renderer-facing runtime API without updating document output.
     */
    callApi: <TData = unknown>(
        definition: PluginDefinition,
        apiName: string,
        _definitions: Map<string, PluginDefinition>,
        params?: Record<string, unknown>,
    ) => Promise<ApiResult<TData>>;

    /** Run the primary action for a definition */
    runPrimary: (
        definition: PluginDefinition,
        definitions: Map<string, PluginDefinition>,
        options?: { actionParams?: Record<string, unknown> },
    ) => Promise<ActionResult | null>;

    /** Run all definitions in order for the focused document */
    runAll: (definitions: Map<string, PluginDefinition>) => Promise<void>;

    /** Run all executable units for a specific document tab */
    runAllForTab: (tabId: string) => Promise<void>;

    /** Cancel the "Run All" batch for the focused document */
    cancelRunAll: () => void;

    /** Cancel the "Run All" batch for a specific document tab */
    cancelRunAllForTab: (tabId: string) => void;

    /** Cancel a running execution */
    cancelExecution: (id: string) => void;

    /** Clear execution state for a unit */
    clearExecution: (id: string) => void;

    /** Clear all execution state */
    clearAll: () => void;

    /**
     * Restore execution entries from parsed definition outputs.
     * Called when opening a document that has cached output blocks.
     */
    restoreFromParsedOutput: (
        definitions: Map<string, PluginDefinition>,
        forDefinitions?: Map<string, import("@magic-md/core").ForDefinition>,
        uses?: import("@magic-md/core").UseBinding[],
        imports?: ImportBinding[],
        contentDefinitions?: Map<
            string,
            import("@magic-md/core").ContentDefinition
        >,
    ) => void;

    /* -- Engine Lifecycle (internal) -- */

    /** Initialize Engine Server connection and start listening for notifications */
    _initEngine: () => Promise<void>;

    /** Create or sync a session for the current document or explicit tab. Throws on failure. */
    _ensureSession: (tabId?: string) => Promise<string>;

    /** Handle an Engine Server notification */
    _handleNotification: (notification: EngineNotification) => void;

    /** Look up primary action name from engine plugin info */
    _getPrimaryAction: (pluginName: string) => string | null;

    /** Cancel all running executions on a background tab */
    cancelBackgroundTabExecutions: (tabId: string) => void;

    /**
     * Reset all execution state when the Engine switches.
     * Closes the current session, clears executions, and marks engine as not ready.
     */
    resetForEngineSwitch: () => void;
}

const defaultEntry: ExecutionEntry = {
    unitId: "",
    status: "idle",
    actionName: null,
    result: null,
    output: null,
    startedAt: null,
    duration: null,
};

type PluginExecutionStoreSet = (
    partial:
        | Partial<PluginExecutionState>
        | ((state: PluginExecutionState) => Partial<PluginExecutionState>),
) => void;

type PluginExecutionStoreGet = () => PluginExecutionState;

async function readUnitOutputFromSessionState(
    sessionId: string,
    unitId: string,
): Promise<ExecutionUnitOutput | undefined> {
    const engine = getPlatformService().engine;
    let rawOutput: ExecutionUnitOutput | undefined;
    try {
        rawOutput = (await engine.sessionGetOutput({ sessionId, unitId })).output;
    } catch {
        // Older gateways may not expose per-unit hydration; fall back below.
    }
    if (!rawOutput) {
        const state = await engine.sessionGetState({ sessionId });
        rawOutput = state.outputs?.[unitId];
    }
    return rawOutput;
}

async function hydrateUnitOutputFromSessionState(
    set: PluginExecutionStoreSet,
    sessionId: string,
    unitId: string,
    options: ApplyHydratedUnitOptions = { mode: "running-or-missing" },
    lease?: EngineOperationLease,
): Promise<void> {
    const rawOutput = await readUnitOutputFromSessionState(sessionId, unitId);
    lease?.assertCurrent();
    if (!rawOutput) return;

    const current = usePluginExecutionStore.getState();
    const next = createHydratedUnitOutputPatch(current, unitId, rawOutput, options);
    if (executionMapRefsChanged(current, next)) {
        set(next);
    }
}

function applyHydratedExecutionUnitOutput(
    set: PluginExecutionStoreSet,
    unitId: string,
    output: ExecutionUnitOutput,
    options: ApplyHydratedUnitOptions = { mode: "running-or-missing" },
): void {
    const current = usePluginExecutionStore.getState();
    const next = createHydratedUnitOutputPatch(current, unitId, output, options);
    if (executionMapRefsChanged(current, next)) {
        set(next);
    }
}

async function hydrateUnitOutputFallbackIfNeeded(
    set: PluginExecutionStoreSet,
    sessionId: string,
    unitId: string,
    options: ApplyHydratedUnitOptions = { mode: "running-or-missing" },
    lease?: EngineOperationLease,
): Promise<void> {
    if (!shouldPollSessionStateFallback()) return;
    try {
        await hydrateUnitOutputFromSessionState(
            set,
            sessionId,
            unitId,
            options,
            lease,
        );
    } catch (error) {
        if (isStaleEngineOperationError(error)) throw error;
        // Ignore — best-effort fallback
    }
}

async function hydrateRunAllOutputsFallbackIfNeeded(
    set: PluginExecutionStoreSet,
    sessionId: string,
    lease?: EngineOperationLease,
): Promise<void> {
    if (!shouldPollSessionStateFallback()) return;
    try {
        const state = await getPlatformService().engine.sessionGetState({ sessionId });
        lease?.assertCurrent();
        if (state.outputs) {
            const current = usePluginExecutionStore.getState();
            if (current.sessionId === sessionId) {
                const next = createHydratedExecutionMaps(current, state.outputs, {
                    mode: "always",
                });
                if (executionMapRefsChanged(current, next)) {
                    set(next);
                }
            } else {
                const nextCache = new Map(current.sessionExecutionCache);
                const snapshot = getOrCreateSnapshot(nextCache, sessionId);
                const next = createHydratedExecutionMaps(snapshot, state.outputs, {
                    mode: "always",
                });
                if (executionMapRefsChanged(snapshot, next)) {
                    snapshot.executions = next.executions;
                    snapshot.forExecutions = next.forExecutions;
                    snapshot.contentExecutions = next.contentExecutions;
                    snapshot.importExecutions = next.importExecutions;
                    snapshot.useExecutions = next.useExecutions;
                    nextCache.set(sessionId, snapshot);
                    set({ sessionExecutionCache: nextCache });
                }
            }
        }
    } catch (error) {
        if (isStaleEngineOperationError(error)) throw error;
        // Ignore — best-effort fallback
    }
}

async function hydrateAllSessionOutputs(
    set: PluginExecutionStoreSet,
    sessionId: string,
    options: ApplyHydratedUnitOptions = { mode: "running-or-missing" },
    context: { refreshedImportNamespace?: string } = {},
    lease?: EngineOperationLease,
): Promise<void> {
    try {
        const state = await getPlatformService().engine.sessionGetState({ sessionId });
        lease?.assertCurrent();
        const {
            engineDefinitions: rawEngineDefinitions,
            engineContentDefinitions: rawEngineContentDefinitions,
            engineForBlocks: rawEngineForBlocks,
        } = buildEngineUnitMaps(state.units);
        const current = usePluginExecutionStore.getState();
        const engineDefinitions = reuseMapIfJsonEntriesUnchanged(
            current.engineDefinitions,
            rawEngineDefinitions,
        );
        const engineContentDefinitions = reuseMapIfJsonEntriesUnchanged(
            current.engineContentDefinitions,
            rawEngineContentDefinitions,
        );
        const engineForBlocks = reuseMapIfJsonEntriesUnchanged(
            current.engineForBlocks,
            rawEngineForBlocks,
        );
        let hydrated = createHydratedExecutionMaps(current, state.outputs, options);
        if (context.refreshedImportNamespace) {
            hydrated = pruneStaleImportedNamespaceExecutions(
                hydrated,
                context.refreshedImportNamespace,
                state.outputs,
            );
        }
        const partial: Partial<PluginExecutionState> = {};
        if (executionMapRefsChanged(current, hydrated)) {
            Object.assign(partial, hydrated);
        }
        if (current.engineDefinitions !== engineDefinitions) {
            partial.engineDefinitions = engineDefinitions;
        }
        if (current.engineContentDefinitions !== engineContentDefinitions) {
            partial.engineContentDefinitions = engineContentDefinitions;
        }
        if (current.engineForBlocks !== engineForBlocks) {
            partial.engineForBlocks = engineForBlocks;
        }
        if (Object.keys(partial).length > 0) {
            set(partial);
        }
    } catch (err) {
        if (isStaleEngineOperationError(err)) throw err;
        frontendLog.warn(
            `[session] Failed to hydrate session outputs: ${err instanceof Error ? err.message : String(err)}`,
            undefined,
            { source: "engine" },
        );
    }
}

/**
 * Rebuild the reactive projection for one Engine session after a transport
 * reconnect. The Engine snapshot is authoritative; active and background tabs
 * follow the same hydration path so a missed notification cannot leave a
 * hidden tab permanently stale.
 */
async function refreshExecutionSessionProjection(
    sessionId: string,
    includeContent = false,
): Promise<SessionGetStateResult> {
    const state = await getPlatformService().engine.sessionGetState({
        sessionId,
        ...(includeContent ? { includeContent: true } : {}),
    });
    sessionRevisionTracker.record(sessionId, state.revision);

    const current = usePluginExecutionStore.getState();
    if (current.sessionId === sessionId) {
        const rawEngineMaps = buildEngineUnitMaps(state.units);
        const engineDefinitions = reuseMapIfJsonEntriesUnchanged(
            current.engineDefinitions,
            rawEngineMaps.engineDefinitions,
        );
        const engineContentDefinitions = reuseMapIfJsonEntriesUnchanged(
            current.engineContentDefinitions,
            rawEngineMaps.engineContentDefinitions,
        );
        const engineForBlocks = reuseMapIfJsonEntriesUnchanged(
            current.engineForBlocks,
            rawEngineMaps.engineForBlocks,
        );
        const hydrated = createHydratedExecutionMaps(current, state.outputs, {
            mode: "always",
        });
        usePluginExecutionStore.setState({
            ...hydrated,
            engineDefinitions,
            engineContentDefinitions,
            engineForBlocks,
        });
        return state;
    }

    const nextCache = new Map(current.sessionExecutionCache);
    const snapshot = getOrCreateSnapshot(nextCache, sessionId);
    const rawEngineMaps = buildEngineUnitMaps(state.units);
    snapshot.engineDefinitions = reuseMapIfJsonEntriesUnchanged(
        snapshot.engineDefinitions,
        rawEngineMaps.engineDefinitions,
    );
    snapshot.engineContentDefinitions = reuseMapIfJsonEntriesUnchanged(
        snapshot.engineContentDefinitions,
        rawEngineMaps.engineContentDefinitions,
    );
    snapshot.engineForBlocks = reuseMapIfJsonEntriesUnchanged(
        snapshot.engineForBlocks,
        rawEngineMaps.engineForBlocks,
    );
    Object.assign(
        snapshot,
        createHydratedExecutionMaps(snapshot, state.outputs, {
            mode: "always",
        }),
    );
    nextCache.set(sessionId, snapshot);
    usePluginExecutionStore.setState({ sessionExecutionCache: nextCache });
    return state;
}

export async function resyncExecutionSessionProjection(
    sessionId: string,
    options: { reconcileContent?: boolean } = {},
): Promise<void> {
    const state = await refreshExecutionSessionProjection(
        sessionId,
        options.reconcileContent === true,
    );
    if (!options.reconcileContent || state.content === undefined) return;

    const editorState = useEditorStore.getState();
    for (const { tabId, sessionId: linkedSessionId } of tabSessionRegistry.entries()) {
        if (linkedSessionId !== sessionId) continue;
        const tab = editorState.tabs.find((candidate) => candidate.id === tabId);
        if (!tab) continue;
        reconcileEngineTabContent({
            tabId,
            sessionId,
            tabTitle: tab.title,
            logPrefix: "transport-reconnect",
            filePath: tab.filePath,
            fallbackContent: tab.content,
            content: state.content,
            cancelPendingSync: true,
            preserveLocalChanges: true,
        });
    }
}

async function prepareExecutionRequest(
    get: PluginExecutionStoreGet,
    tabId?: string,
    lease?: EngineOperationLease,
): Promise<{
    sessionId: string;
    clientConfig: ClientConfigOverlay | undefined;
}> {
    // Capture the active tab before awaiting so editor tab switches do not
    // redirect the pending execution sync to a different document.
    const capturedTabId = tabId ?? getActiveTabId() ?? "";
    const sessionId = await get()._ensureSession(capturedTabId);
    lease?.assertCurrent();
    try {
        await syncTabSessionIfNeeded(capturedTabId, sessionId, lease);
    } catch (error) {
        if (isStaleEngineOperationError(error)) throw error;
        // Ignore sync errors — execution can still proceed with current engine state.
    }
    lease?.assertCurrent();
    const clientConfig = await loadClientExecutionConfig(lease);
    lease?.assertCurrent();
    return { sessionId, clientConfig };
}

function getFocusedOrActiveTabId(): string {
    const editor = useEditorStore.getState();
    return editor.getFocusedTabId?.() ?? getActiveTabId() ?? "";
}

function isRunAllRunningForSession(
    state: PluginExecutionState,
    sessionId: string,
): boolean {
    if (state.sessionId === sessionId) {
        return state.isRunningAll;
    }
    return state.sessionExecutionCache.get(sessionId)?.runAllState?.isRunning ?? false;
}

function updateRunAllSessionState(
    set: PluginExecutionStoreSet,
    sessionId: string,
    runAllState: SessionExecutionSnapshot["runAllState"],
    activeRunAllExecutionId: string | null,
): void {
    const current = usePluginExecutionStore.getState();
    const nextCache = new Map(current.sessionExecutionCache);
    const snapshot =
        current.sessionId === sessionId
            ? saveActiveToSnapshot(current)
            : getOrCreateSnapshot(nextCache, sessionId);

    snapshot.runAllState = runAllState;
    snapshot.activeRunAllExecutionId = activeRunAllExecutionId;
    nextCache.set(sessionId, snapshot);

    const patch: Partial<PluginExecutionState> = {
        sessionExecutionCache: nextCache,
    };

    if (current.sessionId === sessionId) {
        patch.isRunningAll = runAllState?.isRunning ?? false;
        patch.runAllProgress = runAllState?.progress ?? null;
        patch.activeRunAllExecutionId = activeRunAllExecutionId;
    }

    set(patch);
    _syncBackgroundRunningTabs();
}

function setRunAllPendingTab(
    set: PluginExecutionStoreSet,
    tabId: string,
    pending: boolean,
): void {
    if (!tabId) return;
    set((state) => {
        const hasPending = state.runAllPendingTabIds.has(tabId);
        if (hasPending === pending) return {};
        const next = new Set(state.runAllPendingTabIds);
        if (pending) {
            next.add(tabId);
        } else {
            next.delete(tabId);
        }
        return { runAllPendingTabIds: next };
    });
    _syncBackgroundRunningTabs();
}

function buildCancelActiveRunAllPatch(
    state: PluginExecutionState,
): Partial<PluginExecutionState> {
    const nextExecutions = new Map(state.executions);
    for (const [id, entry] of nextExecutions) {
        if (entry.status === "running") {
            nextExecutions.set(id, {
                ...entry,
                status: "idle",
                duration: entry.startedAt ? Date.now() - entry.startedAt : null,
            });
        }
    }

    const nextForExecutions = new Map(state.forExecutions);
    for (const [id, entry] of nextForExecutions) {
        if (entry.status === "running") {
            nextForExecutions.set(id, {
                ...entry,
                status: "idle",
                duration: entry.startedAt ? Date.now() - entry.startedAt : null,
            });
        }
    }

    const nextContentExecutions = new Map(state.contentExecutions);
    for (const [id, entry] of nextContentExecutions) {
        if (entry.status === "running") {
            nextContentExecutions.set(id, {
                ...entry,
                status: "idle",
                duration: entry.startedAt ? Date.now() - entry.startedAt : null,
            });
        }
    }

    const nextImportExecutions = new Map(state.importExecutions);
    for (const [id, entry] of nextImportExecutions) {
        if (entry.status === "running") {
            nextImportExecutions.set(id, {
                ...entry,
                status: "idle",
                duration: entry.startedAt ? Date.now() - entry.startedAt : null,
            });
        }
    }

    const nextUseExecutions = new Map(state.useExecutions);
    for (const [id, entry] of nextUseExecutions) {
        if (entry.status === "running") {
            nextUseExecutions.set(id, {
                ...entry,
                status: "idle",
                duration: entry.startedAt ? Date.now() - entry.startedAt : null,
            });
        }
    }

    return {
        executions: nextExecutions,
        forExecutions: nextForExecutions,
        contentExecutions: nextContentExecutions,
        importExecutions: nextImportExecutions,
        useExecutions: nextUseExecutions,
        isRunningAll: false,
        runAllProgress: null,
        activeRunAllExecutionId: null,
    };
}

async function refreshDocumentBundleForFilePath(
    filePath: string,
    lease?: EngineOperationLease,
): Promise<void> {
    const editor = useEditorStore.getState();
    if (!filePath || !/\.md$/i.test(filePath)) return;

    const bundleDir = getDocumentBundleDir(filePath);

    // When the executed file lives inside a parent document's .magic-md/assets/
    // (e.g. xx.magic-md/assets/yy.md), calling refreshSubtree on the executed
    // file's own bundle dir (yy.magic-md) triggers a fallback that refreshes
    // the workspace root.  That root-level refresh rebuilds all top-level
    // children and the subsequent merge can lose the expanded / loaded state
    // of unrelated tree nodes, causing virtual children under the parent
    // document (xx.md) to disappear.
    //
    // Avoid the fallback by refreshing the parent document's bundle dir and
    // the immediate assets directory instead — both of which are already
    // present in the tree when the parent document is expanded.
    const parentDocPath = getDocumentPathFromAssetsPath(filePath);

    if (parentDocPath) {
        // Refresh the parent document's bundle so xx.md's virtual children
        // reflect any new outputs from the executed asset file.
        const parentBundleDir = getDocumentBundleDir(parentDocPath);
        await editor.refreshSubtree(parentBundleDir).catch(() => {});
        lease?.assertCurrent();

        // Refresh the assets directory so the executed file's tree node
        // picks up a newly-created bundle dir (yy.magic-md) as its bundlePath.
        const assetsDir = filePath.substring(0, filePath.lastIndexOf("/"));
        if (assetsDir) {
            await editor.refreshSubtree(assetsDir).catch(() => {});
            lease?.assertCurrent();
        }
    } else {
        await editor.refreshSubtree(bundleDir).catch(() => {});
        lease?.assertCurrent();
    }

    lease?.assertCurrent();
    if (typeof window !== "undefined") {
        window.dispatchEvent(
            new CustomEvent("magic-md:bundle-refresh", {
                detail: { filePath, bundleDir },
            }),
        );
    }
}

async function refreshDocumentBundleAfterExecution(
    sessionId: string,
    lease?: EngineOperationLease,
): Promise<void> {
    const editor = useEditorStore.getState();
    const ownerTabId = tabSessionRegistry.getTabForSession(sessionId) ?? getActiveTabId();
    const filePath = ownerTabId
        ? editor.tabs.find((tab) => tab.id === ownerTabId)?.filePath
        : undefined;
    if (!filePath) return;
    await refreshDocumentBundleForFilePath(filePath, lease);
}

interface RunNonDefinitionUnitOptions {
    unitId: string;
    action: "compute" | "resolve" | "materialize";
    logLabel: string;
    markRunning: (state: PluginExecutionState) => Partial<PluginExecutionState>;
    markError: (
        state: PluginExecutionState,
        errMsg: string,
    ) => Partial<PluginExecutionState>;
}

async function runNonDefinitionUnitAction(
    set: PluginExecutionStoreSet,
    get: PluginExecutionStoreGet,
    options: RunNonDefinitionUnitOptions,
): Promise<void> {
    const engine = getPlatformService().engine;
    const lease = engineOperationController.capture();
    let trackedExecutionId: string | null = null;

    try {
        set((state) => options.markRunning(state));

        const { sessionId, clientConfig } = await prepareExecutionRequest(
            get,
            undefined,
            lease,
        );
        const started = await waitForEnginePromise(
            engine.executionStart({
                sessionId,
                target: "unit",
                unitId: options.unitId,
                action: options.action,
                clientConfig,
            }),
            lease,
        );
        lease.assertCurrent();
        trackedExecutionId = started.executionId;
        setActiveExecutionId(options.unitId, started.executionId);
        const result = await executionJobWaiter.wait(started.executionId, lease);
        lease.assertCurrent();

        if (result.status === "cancelled") {
            return;
        }
        if (!result.success) {
            throw new Error(
                result.error ??
                result.message ??
                `${options.logLabel} execution failed`,
            );
        }

        const directOutput = result.output;
        if (directOutput !== undefined) {
            await refreshDocumentBundleAfterExecution(sessionId, lease);
            lease.assertCurrent();
            applyHydratedExecutionUnitOutput(set, options.unitId, directOutput, {
                mode: "always",
            });
            if (directOutput.unitType === "import") {
                await hydrateAllSessionOutputs(
                    set,
                    sessionId,
                    { mode: "always" },
                    { refreshedImportNamespace: options.unitId },
                    lease,
                );
            }
            return;
        }

        if (options.action === "resolve") {
            await hydrateAllSessionOutputs(
                set,
                sessionId,
                { mode: "always" },
                { refreshedImportNamespace: options.unitId },
                lease,
            );
        } else {
            await hydrateUnitOutputFallbackIfNeeded(set, sessionId, options.unitId, {
                mode: "always",
            }, lease);
        }
    } catch (err) {
        if (isStaleEngineOperationError(err)) return;
        const errMsg = err instanceof Error ? err.message : String(err);
        useLogStore
            .getState()
            .error(`${options.logLabel} execution failed: ${options.unitId} — ${errMsg}`, {
                source: "engine",
        });
        set((state) => options.markError(state, errMsg));
    } finally {
        if (trackedExecutionId) {
            clearActiveExecutionId(options.unitId, trackedExecutionId);
        }
    }
}

async function readDocumentContentForPath(
    filePath: string,
    engine: EngineService,
): Promise<string> {
    const openTab = useEditorStore
        .getState()
        .tabs.find((tab) => tab.filePath === filePath);
    if (openTab) return openTab.content;
    return (await engine.workspaceReadFile({ path: filePath })).content;
}

async function persistSourceDocumentContent(
    filePath: string,
    content: string | null,
    engine: EngineService,
    lease: EngineOperationLease,
): Promise<void> {
    if (content == null) return;
    await engine.workspaceWriteFile({ path: filePath, content });
    lease.assertCurrent();
    const sourceTab = useEditorStore
        .getState()
        .tabs.find((tab) => tab.filePath === filePath);
    if (sourceTab) {
        useEditorStore.getState().setExternalContent(content, sourceTab.id);
    }
}

async function runImportedSourceUnitAction(
    set: PluginExecutionStoreSet,
    get: PluginExecutionStoreGet,
    options: RunImportedSourceUnitOptions,
): Promise<ActionResult> {
    const engine = getPlatformService().engine;
    const lease = engineOperationController.capture();
    const log = useLogStore.getState();
    let actionName = options.actionName ?? "compute";
    const startedAt = Date.now();
    let sourceSessionId: string | null = null;
    let shouldCloseSourceSession = false;
    let latestSourceContent: string | null = null;
    let unsubscribeDocumentChanged: (() => void) | null = null;
    let trackedExecutionId: string | null = null;

    try {
        if (!get().engineReady) {
            await get()._initEngine();
            lease.assertCurrent();
        }
        actionName =
            options.actionName ??
            (options.pluginName ? get()._getPrimaryAction(options.pluginName) : null) ??
            "compute";

        set((state) =>
            markImportedSourceRenderedUnitRunning(
                state,
                options,
                actionName,
                startedAt,
            ),
        );

        const sourceContent = await readDocumentContentForPath(
            options.sourceDocumentPath,
            engine,
        );
        lease.assertCurrent();
        const sourceSession = await waitForEnginePromise(
            engine.sessionCreate({
                filePath: options.sourceDocumentPath,
                content: sourceContent,
                includeOutputs: shouldHydrateSessionOutputsForContent(sourceContent),
                includePresentationContent: false,
            }),
            lease,
        );
        lease.assertCurrent();
        sourceSessionId = sourceSession.sessionId;
        shouldCloseSourceSession = !tabSessionRegistry.getTabForSession(sourceSessionId);

        unsubscribeDocumentChanged = getNotificationBus().subscribe<DocumentChangedEvent>(
            "event/document.changed",
            (event) => {
                if (!lease.isCurrent()) return;
                if (event.sessionId !== sourceSessionId) return;
                if (
                    event.filePath &&
                    event.filePath !== options.sourceDocumentPath
                ) return;
                if (typeof event.content === "string") {
                    latestSourceContent = event.content;
                }
            },
        );

        const clientConfig = await loadClientExecutionConfig(lease);
        lease.assertCurrent();
        const started = await waitForEnginePromise(
            engine.executionStart({
                sessionId: sourceSessionId,
                target: "unit",
                unitId: options.sourceUnitId,
                action: actionName,
                actionParams: options.actionParams,
                clientConfig,
            }),
            lease,
        );
        lease.assertCurrent();
        trackedExecutionId = started.executionId;
        setActiveExecutionId(options.renderedUnitId, started.executionId);

        const result = await executionJobWaiter.wait(started.executionId, lease);
        lease.assertCurrent();
        if (result.status === "cancelled") {
            return {
                success: false,
                message: result.message ?? "Execution cancelled by user",
            };
        }
        if (!result.success) {
            throw new Error(
                result.error ??
                result.message ??
                `Imported source execution failed: ${options.sourceUnitId}`,
            );
        }

        await persistSourceDocumentContent(
            options.sourceDocumentPath,
            latestSourceContent,
            engine,
            lease,
        );
        await refreshDocumentBundleForFilePath(
            options.sourceDocumentPath,
            lease,
        );

        await get().runImportAction(options.importNamespace);
        lease.assertCurrent();

        const finalState = get();
        if (isImportedRenderedUnitStillRunning(finalState, options)) {
            const missingMessage = `Imported output not found after re-import: ${options.renderedUnitId}`;
            set((state) =>
                markImportedSourceRenderedUnitError(
                    state,
                    options,
                    actionName,
                    missingMessage,
                ),
            );
            return { success: false, message: missingMessage };
        }

        if ((options.unitKind ?? "node") === "node") {
            return getImportedRenderedNodeResult(get(), options.renderedUnitId);
        }
        return { success: true };
    } catch (err) {
        if (isStaleEngineOperationError(err)) {
            return {
                success: false,
                message: ENGINE_SWITCHED_DURING_EXECUTION_MESSAGE,
            };
        }
        const errMsg = err instanceof Error ? err.message : String(err);
        log.error(
            `Imported source execution failed: ${options.renderedUnitId} — ${errMsg}`,
            {
                source: "engine",
                unitId: options.renderedUnitId,
                pluginName: options.pluginName,
            },
        );
        set((state) =>
            markImportedSourceRenderedUnitError(
                state,
                options,
                actionName,
                errMsg,
            ),
        );
        return { success: false, message: errMsg };
    } finally {
        unsubscribeDocumentChanged?.();
        if (trackedExecutionId) {
            clearActiveExecutionId(
                options.renderedUnitId,
                trackedExecutionId,
            );
        }
        if (sourceSessionId && shouldCloseSourceSession) {
            engine.sessionClose(sourceSessionId).catch(() => { });
        }
    }
}

/* -- Module-level state for session sync (shared with resetForEngineSwitch) -- */
const engineInitController = new EngineInitController();
const tabSessionCreationController = new TabSessionCreationController();
let disposeNotificationHandlers: (() => void) | null = null;
let notificationHandlersBus: ReturnType<typeof getNotificationBus> | null = null;
const tabSessionSyncTracker = new TabSessionSyncTracker();
const externalConfigResyncTails = new Map<string, Promise<void>>();
let rendererComputeActionQueue: Promise<void> = Promise.resolve();

function enqueueRendererComputeAction<T>(task: () => Promise<T>): Promise<T> {
    const run = rendererComputeActionQueue.then(task, task);
    rendererComputeActionQueue = run.then(
        () => undefined,
        () => undefined,
    );
    return run;
}

function resetRendererComputeActionQueue(): void {
    rendererComputeActionQueue = Promise.resolve();
}

function shouldQueueRendererComputeAction(
    definition: PluginDefinition,
    actionName: string,
    options: { actionParams?: Record<string, unknown> } | undefined,
): boolean {
    return actionName === "compute" &&
        !!options?.actionParams &&
        (definition.pluginName === "inputs" || definition.pluginName === "control");
}

function setActiveExecutionId(
    unitId: string,
    executionId: string,
): void {
    const state = usePluginExecutionStore.getState();
    const current = state.activeExecutionIdsByUnit.get(unitId);
    if (current === executionId) return;
    const next = new Map(state.activeExecutionIdsByUnit);
    next.set(unitId, executionId);
    usePluginExecutionStore.setState({ activeExecutionIdsByUnit: next });
}

function clearActiveExecutionId(
    unitId: string,
    executionId: string,
): void {
    const state = usePluginExecutionStore.getState();
    if (state.activeExecutionIdsByUnit.get(unitId) !== executionId) return;
    const next = new Map(state.activeExecutionIdsByUnit);
    next.delete(unitId);
    usePluginExecutionStore.setState({ activeExecutionIdsByUnit: next });
}

/**
 * Recalculate which background tabs have running executions
 * and update the Zustand state if changed.
 * Uses the session-keyed execution cache.
 */
export function _syncBackgroundRunningTabs() {
    const activeTabId = getActiveTabId() ?? "";
    const state = usePluginExecutionStore.getState();
    const runningTabs = collectBackgroundRunningTabs({
        activeTabId,
        runAllPendingTabIds: state.runAllPendingTabIds,
        tabSessions: tabSessionRegistry.entries(),
        sessionExecutionCache: state.sessionExecutionCache,
    });

    if (!equalStringSets(runningTabs, state.backgroundRunningTabs)) {
        usePluginExecutionStore.setState({ backgroundRunningTabs: runningTabs });
    }
}

function getTabContent(tabId: string): string | null {
    return (
        useEditorStore.getState().tabs.find((tab) => tab.id === tabId)?.content ??
        null
    );
}

function getTabSessionId(tabId: string): string | null {
    return tabSessionRegistry.getSessionForTab(tabId) ?? null;
}

function markTabSessionSynced(
    tabId: string,
    sessionId: string,
    content: string,
): void {
    tabSessionSyncTracker.mark(tabId, sessionId, content);
    tabPrevContentMap.set(tabId, content);
}

function bumpTabSyncVersion(tabId: string): void {
    usePluginExecutionStore.setState((store) => {
        const nextSyncVersions = new Map(store.tabSyncVersionMap);
        nextSyncVersions.set(tabId, (nextSyncVersions.get(tabId) ?? 0) + 1);
        return { tabSyncVersionMap: nextSyncVersions };
    });
}

function clearPendingTabSync(tabId: string): void {
    const pendingSyncTimer = tabSyncTimerMap.get(tabId);
    if (pendingSyncTimer) {
        clearTimeout(pendingSyncTimer);
        tabSyncTimerMap.delete(tabId);
    }
}

interface ReconcileEngineTabContentOptions {
    tabId: string;
    sessionId: string;
    tabTitle: string;
    logPrefix: string;
    filePath?: string;
    fallbackContent: string;
    content?: string;
    presentationContent?: string;
    cancelPendingSync?: boolean;
    preserveLocalChanges?: boolean;
}

function preserveLocalConflictCopy(tab: EditorTab, content: string): void {
    const conflictCopyTitle = translate(
        "editor",
        "sessionSync.conflictCopyTitle",
        { title: tab.title },
    );
    useEditorStore.getState().addTab({
        title: conflictCopyTitle,
        content,
        isDirty: true,
        kind: "document",
    });
    useEditorStore.getState().setStatusMessage(
        translate("editor", "sessionSync.conflictRecovered", {
            title: tab.title,
            copyTitle: conflictCopyTitle,
        }),
        8_000,
    );
}

export function reconcileEngineTabContent(
    options: ReconcileEngineTabContentOptions,
): void {
    const editorState = useEditorStore.getState();
    const tab = editorState.tabs.find((entry) => entry.id === options.tabId);
    if (!tab) return;

    if (options.cancelPendingSync) {
        clearPendingTabSync(options.tabId);
    }

    const resolved = resolveEngineDisplayContent(
        {
            content: options.content,
            presentationContent: options.presentationContent,
        },
        options.fallbackContent,
    );
    const lastSyncedContent = tabSessionSyncTracker.getContent(
        options.tabId,
        options.sessionId,
    );
    const hasLocalChanges =
        lastSyncedContent === undefined
            ? tab.isDirty
            : lastSyncedContent !== tab.content;
    if (
        options.preserveLocalChanges &&
        resolved.content !== tab.content &&
        hasLocalChanges
    ) {
        preserveLocalConflictCopy(tab, tab.content);
    }
    markTabSessionSynced(options.tabId, options.sessionId, resolved.content);

    if (resolved.content === tab.content) return;

    frontendLog.debug(
        `[${options.logPrefix}] session=${options.sessionId} filePath=${options.filePath ?? "none"} → tab=${options.tabId} (${options.tabTitle}) contentLen=${resolved.content.length}${resolved.isPresentation ? " (presentation)" : ""}`,
        undefined,
        { source: "engine" },
    );

    editorState.setExternalContent(resolved.content, options.tabId);
}

function isTabSessionSynced(
    tabId: string,
    sessionId: string,
    content: string,
): boolean {
    return tabSessionSyncTracker.isSynced(tabId, sessionId, content);
}

function shouldPollSessionStateFallback(): boolean {
    return !getNotificationBus().connected;
}

function isSessionRevisionConflict(error: unknown): boolean {
    if (
        error !== null &&
        typeof error === "object" &&
        "code" in error &&
        (error as { code?: unknown }).code ===
            ErrorCodes.SessionRevisionConflict
    ) {
        return true;
    }
    const message = error instanceof Error ? error.message : String(error);
    return message.includes("Session revision conflict");
}

async function syncTabSessionIfNeeded(
    tabId: string,
    sessionId: string,
    lease?: EngineOperationLease,
): Promise<void> {
    const content = getTabContent(tabId);
    if (content == null || isTabSessionSynced(tabId, sessionId, content)) {
        return;
    }

    // Re-verify that this tab still maps to this session.
    // The timer that scheduled this call captured sessionId at schedule
    // time; if tab ownership changed since then, syncing stale content
    // to the old session would corrupt it (see cross-pane write bug).
    const currentSessionId = tabSessionRegistry.getSessionForTab(tabId);
    if (currentSessionId !== sessionId) {
        frontendLog.debug(
            `[session-sync] Aborted stale sync: tab=${tabId} expected session=${sessionId} but current=${currentSessionId ?? "null"}`,
            undefined,
            { source: "engine" },
        );
        return;
    }

    const engine = getPlatformService().engine;
    const baseRevision = sessionRevisionTracker.get(sessionId);
    let syncResult: SessionSyncResult;
    try {
        syncResult = await engine.sessionSync({
            sessionId,
            content,
            includeOutputs: shouldHydrateSessionOutputsForContent(content),
            ...(baseRevision === undefined ? {} : { baseRevision }),
        });
    } catch (error) {
        if (!isSessionRevisionConflict(error)) throw error;

        // Keep the file-backed tab aligned with Engine authority while saving
        // the rejected local text in a dirty untitled tab. Mark the authority
        // synced so the subscriber does not retry the stale write indefinitely.
        const state = await refreshExecutionSessionProjection(sessionId, true);
        lease?.assertCurrent();
        const currentSessionId = tabSessionRegistry.getSessionForTab(tabId);
        const currentContent = getTabContent(tabId);
        if (currentSessionId !== sessionId || state.content === undefined) {
            throw error;
        }
        if (currentContent !== content) {
            if (
                currentContent !== null &&
                isTabSessionSynced(tabId, sessionId, currentContent)
            ) {
                return;
            }
            throw error;
        }
        const tab = useEditorStore
            .getState()
            .tabs.find((candidate) => candidate.id === tabId);
        if (!tab) throw error;
        const hasConflictingLocalEdits = state.content !== content;
        if (hasConflictingLocalEdits) {
            preserveLocalConflictCopy(tab, content);
        }
        reconcileEngineTabContent({
            tabId,
            sessionId,
            tabTitle: tab.title,
            logPrefix: "session-sync-conflict",
            filePath: tab.filePath,
            fallbackContent: content,
            content: state.content,
            cancelPendingSync: true,
        });
        bumpTabSyncVersion(tabId);
        frontendLog.warn(
            `[session-sync] Rejected stale revision ${baseRevision ?? "unknown"}; reloaded Engine revision ${state.revision ?? "unknown"} for tab=${tabId}`,
            undefined,
            { source: "engine" },
        );
        return;
    }
    lease?.assertCurrent();
    sessionRevisionTracker.record(sessionId, syncResult.revision);

    // After the RPC completes, verify that the tab content hasn't been
    // updated by a document.changed event while we were in-flight.
    // If it has, don't overwrite the newer sync state with our stale
    // content — the session-sync subscriber will re-fire with the
    // correct content.
    const postSyncContent = getTabContent(tabId);
    if (postSyncContent !== content) {
        frontendLog.debug(
            `[session-sync] Skipping stale markSynced: tab=${tabId} content changed during in-flight sync`,
            undefined,
            { source: "engine" },
        );
        return;
    }
    markTabSessionSynced(tabId, sessionId, content);
    bumpTabSyncVersion(tabId);

    if (
        getActiveTabId() === tabId &&
        usePluginExecutionStore.getState().sessionId === sessionId &&
        syncResult?.units
    ) {
        const changedUnitIds = new Set(syncResult.changedUnitIds ?? []);
        const engineMaps = buildEngineUnitMaps(syncResult.units);
        const staleClearedMaps = changedUnitIds.size > 0
            ? clearChangedUnitExecutions(usePluginExecutionStore.getState(), changedUnitIds)
            : undefined;
        const hydratedOutputs = withoutChangedOutputs(
            syncResult.outputs,
            changedUnitIds,
        );
        const hydratedMaps = syncResult.outputs
            ? createHydratedExecutionMaps(
                staleClearedMaps ?? usePluginExecutionStore.getState(),
                hydratedOutputs,
                { mode: "running-or-missing" },
            )
            : undefined;
        usePluginExecutionStore.setState({
            ...(staleClearedMaps ?? {}),
            ...engineMaps,
            ...(hydratedMaps ?? {}),
        });
    } else if (syncResult?.units) {
        // Background tab sync: save engine definitions to session cache
        // so they're available when the user switches to this tab.
        const currentSessionForTab = tabSessionRegistry.getSessionForTab(tabId);
        if (currentSessionForTab === sessionId) {
            const changedUnitIds = new Set(syncResult.changedUnitIds ?? []);
            const { engineDefinitions, engineContentDefinitions, engineForBlocks } = buildEngineUnitMaps(syncResult.units);
            const nextCache = new Map(usePluginExecutionStore.getState().sessionExecutionCache);
            const snapshot = getOrCreateSnapshot(nextCache, sessionId);
            const staleClearedMaps = changedUnitIds.size > 0
                ? clearChangedUnitExecutions(snapshot, changedUnitIds)
                : undefined;
            snapshot.engineDefinitions = engineDefinitions;
            snapshot.engineContentDefinitions = engineContentDefinitions;
            snapshot.engineForBlocks = engineForBlocks;
            if (staleClearedMaps) {
                snapshot.executions = staleClearedMaps.executions;
                snapshot.forExecutions = staleClearedMaps.forExecutions;
                snapshot.contentExecutions = staleClearedMaps.contentExecutions;
                snapshot.importExecutions = staleClearedMaps.importExecutions;
                snapshot.useExecutions = staleClearedMaps.useExecutions;
            }
            if (syncResult.outputs) {
                const hydratedOutputs = withoutChangedOutputs(
                    syncResult.outputs,
                    changedUnitIds,
                );
                Object.assign(
                    snapshot,
                    createHydratedExecutionMaps(snapshot, hydratedOutputs, {
                        mode: "running-or-missing",
                    }),
                );
            }
            nextCache.set(sessionId, snapshot);
            usePluginExecutionStore.setState({ sessionExecutionCache: nextCache });
        }
    }
}

/** Rehydrate open sessions when a stable external-config file changes. */
export async function resyncExternalConfigFile(filePath: string): Promise<void> {
    const normalized = filePath.replace(/\\/g, "/");
    const marker = "/configs/";
    const markerIndex = normalized.lastIndexOf(marker);
    if (markerIndex < 0) return;
    const bundleDir = normalized.slice(0, markerIndex);
    const locator = normalized.slice(markerIndex + marker.length);
    if (
        !bundleDir.toLowerCase().endsWith(".magic-md") ||
        !/^[^/]+\.json\.br$/i.test(locator)
    ) {
        return;
    }

    const tasks: Promise<void>[] = [];
    for (const tab of useEditorStore.getState().tabs) {
        if (!tab.filePath || getDocumentBundleDir(tab.filePath) !== bundleDir) continue;
        const sessionId = tabSessionRegistry.getSessionForTab(tab.id);
        if (!sessionId) continue;
        const prior = externalConfigResyncTails.get(tab.id) ?? Promise.resolve();
        const current = prior
            .catch(() => undefined)
            .then(async () => {
                tabSessionSyncTracker.delete(tab.id);
                await syncTabSessionIfNeeded(tab.id, sessionId);
            });
        externalConfigResyncTails.set(tab.id, current);
        tasks.push(
            current.finally(() => {
                if (externalConfigResyncTails.get(tab.id) === current) {
                    externalConfigResyncTails.delete(tab.id);
                }
            }),
        );
    }
    await Promise.all(tasks);
}

export const usePluginExecutionStore = create<PluginExecutionState>(
    (set, get) => ({
        executions: new Map(),
        isRunningAll: false,
        runAllProgress: null,
        activeRunAllExecutionId: null,
        activeExecutionIdsByUnit: new Map(),
        sessionId: null,
        executionError: null,
        engineReady: false,
        enginePlugins: [],
        engineDefinitions: new Map(),
        engineContentDefinitions: new Map(),
        engineForBlocks: new Map(),
        backgroundRunningTabs: new Set<string>(),
        runAllPendingTabIds: new Set<string>(),

        /* Per-tab / session reactive state */
        tabSessionMap: new Map(),
        sessionExecutionCache: new Map(),
        tabSyncVersionMap: new Map(),
        visibleTabIds: new Set(),

        /* For-loop execution state */
        forExecutions: new Map(),

        /* Content-block execution state */
        contentExecutions: new Map(),

        /* Import-block execution state */
        importExecutions: new Map(),

        /* Use-block execution state */
        useExecutions: new Map(),

        getForExecution: (forId) => {
            return (
                get().forExecutions.get(forId) ?? {
                    forId,
                    status: "idle",
                    totalIterations: 0,
                    completedIterations: 0,
                    iterations: [],
                    startedAt: null,
                    duration: null,
                }
            );
        },

        runForAction: async (forId: string) =>
            runNonDefinitionUnitAction(set, get, {
                unitId: forId,
                action: "compute",
                logLabel: "For-loop",
                markRunning: (state) => {
                    const next = new Map(state.forExecutions);
                    next.set(forId, {
                        forId,
                        status: "running",
                        totalIterations: 0,
                        completedIterations: 0,
                        iterations: [],
                        startedAt: Date.now(),
                        duration: null,
                    });
                    return { forExecutions: next };
                },
                markError: (state, errMsg) => {
                    const next = new Map(state.forExecutions);
                    const prev = next.get(forId);
                    next.set(forId, {
                        forId,
                        status: "error",
                        totalIterations: prev?.totalIterations ?? 0,
                        completedIterations: prev?.completedIterations ?? 0,
                        iterations: prev?.iterations ?? [],
                        startedAt: prev?.startedAt ?? null,
                        duration: prev?.startedAt ? Date.now() - prev.startedAt : null,
                        error: errMsg,
                    });
                    return { forExecutions: next };
                },
            }),

        runContentAction: async (contentId: string) =>
            runNonDefinitionUnitAction(set, get, {
                unitId: contentId,
                action: "compute",
                logLabel: "Content-block",
                markRunning: (state) => {
                    const next = new Map(state.contentExecutions);
                    next.set(contentId, {
                        contentId,
                        status: "running",
                        startedAt: Date.now(),
                        duration: null,
                    });
                    return { contentExecutions: next };
                },
                markError: (state, errMsg) => {
                    const next = new Map(state.contentExecutions);
                    const prev = next.get(contentId);
                    next.set(contentId, {
                        contentId,
                        status: "error",
                        startedAt: prev?.startedAt ?? null,
                        duration: prev?.startedAt ? Date.now() - prev.startedAt : null,
                        error: errMsg,
                    });
                    return { contentExecutions: next };
                },
            }),

        runImportAction: async (importId: string) =>
            runNonDefinitionUnitAction(set, get, {
                unitId: importId,
                action: "resolve",
                logLabel: "Import",
                markRunning: (state) => {
                    const next = new Map(state.importExecutions);
                    const prev = next.get(importId);
                    next.set(importId, {
                        importId,
                        status: "running",
                        startedAt: Date.now(),
                        duration: null,
                        ...(prev?.symbols ? { symbols: prev.symbols } : {}),
                        ...(prev?.document ? { document: prev.document } : {}),
                        ...(prev?.meta ? { meta: prev.meta } : {}),
                    });
                    return { importExecutions: next };
                },
                markError: (state, errMsg) => {
                    const next = new Map(state.importExecutions);
                    const prev = next.get(importId);
                    next.set(importId, {
                        importId,
                        status: "error",
                        startedAt: prev?.startedAt ?? null,
                        duration: prev?.startedAt ? Date.now() - prev.startedAt : null,
                        error: errMsg,
                        ...(prev?.symbols ? { symbols: prev.symbols } : {}),
                        ...(prev?.document ? { document: prev.document } : {}),
                        ...(prev?.meta ? { meta: prev.meta } : {}),
                    });
                    return { importExecutions: next };
                },
            }),

        runImportedSourceAction: async (options) =>
            runImportedSourceUnitAction(set, get, options),

        runUseAction: async (useId: string) =>
            runNonDefinitionUnitAction(set, get, {
                unitId: useId,
                action: "materialize",
                logLabel: "Use",
                markRunning: (state) => {
                    const next = new Map(state.useExecutions);
                    next.set(useId, {
                        useId,
                        status: "running",
                        startedAt: Date.now(),
                        duration: null,
                    });
                    return { useExecutions: next };
                },
                markError: (state, errMsg) => {
                    const next = new Map(state.useExecutions);
                    const prev = next.get(useId);
                    next.set(useId, {
                        useId,
                        status: "error",
                        startedAt: prev?.startedAt ?? null,
                        duration: prev?.startedAt ? Date.now() - prev.startedAt : null,
                        error: errMsg,
                    });
                    return { useExecutions: next };
                },
            }),

        getExecution: (id) => {
            return get().executions.get(id) ?? { ...defaultEntry, unitId: id };
        },

        getOutput: (id) => {
            return get().executions.get(id)?.output ?? null;
        },

        hydrateOutput: async (unitId) => {
            const capturedTabId = getActiveTabId() ?? "";
            let sessionId =
                (capturedTabId ? getTabSessionId(capturedTabId) : null) ??
                get().sessionId;

            if (!sessionId) {
                sessionId = await get()._ensureSession();
            }
            if (!sessionId) return;

            const key = `${sessionId}\u0000${unitId}`;
            const inflight = lazyOutputHydrationPromises.get(key);
            if (inflight) return inflight;

            const lease = engineOperationController.capture();
            let request!: Promise<void>;
            request = (async () => {
                const rawOutput = await readUnitOutputFromSessionState(sessionId, unitId);
                lease.assertCurrent();
                if (!rawOutput) return;

                const currentSessionForTab = capturedTabId
                    ? getTabSessionId(capturedTabId)
                    : null;
                const activeTabId = getActiveTabId() ?? "";
                if (capturedTabId && currentSessionForTab !== sessionId) return;

                if (
                    capturedTabId &&
                    activeTabId === capturedTabId &&
                    get().sessionId === sessionId
                ) {
                    applyHydratedExecutionUnitOutput(set, unitId, rawOutput, {
                        mode: "missing",
                    });
                    if (rawOutput.unitType === "import") {
                        await hydrateAllSessionOutputs(set, sessionId, {
                            mode: "missing",
                        }, {}, lease);
                    }
                    return;
                }

                lease.assertCurrent();
                set((store) => {
                    const nextCache = new Map(store.sessionExecutionCache);
                    const snapshot = getOrCreateSnapshot(nextCache, sessionId);
                    Object.assign(
                        snapshot,
                        createHydratedUnitOutputPatch(snapshot, unitId, rawOutput, {
                            mode: "missing",
                        }),
                    );
                    nextCache.set(sessionId, snapshot);
                    return { sessionExecutionCache: nextCache };
                });
            })().catch((error) => {
                if (!isStaleEngineOperationError(error)) throw error;
            }).finally(() => {
                if (lazyOutputHydrationPromises.get(key) === request) {
                    lazyOutputHydrationPromises.delete(key);
                }
            });

            lazyOutputHydrationPromises.set(key, request);
            return request;
        },

        callApi: async <TData = unknown,>(
            definition: PluginDefinition,
            apiName: string,
            _definitions: Map<string, PluginDefinition>,
            params?: Record<string, unknown>,
        ): Promise<ApiResult<TData>> => {
            const engine = getPlatformService().engine;
            const lease = engineOperationController.capture();
            if (!engine.pluginApiCall) {
                return {
                    success: false,
                    message: "Plugin runtime API is not supported by this engine",
                };
            }
            try {
                const { sessionId, clientConfig } =
                    await prepareExecutionRequest(get, undefined, lease);
                const result = await engine.pluginApiCall<TData>({
                    sessionId,
                    unitId: definition.id,
                    api: apiName,
                    params,
                    clientConfig,
                });
                lease.assertCurrent();
                return result;
            } catch (err) {
                return {
                    success: false,
                    message: err instanceof Error ? err.message : String(err),
                };
            }
        },

        runAction: async (definition, actionName, _definitions, _options) => {
            const executeAction = async () => {
            const log = useLogStore.getState();
            const engine = getPlatformService().engine;
            const lease = engineOperationController.capture();
            let trackedExecutionId: string | null = null;
            let tracksPrimaryExecution = true;

            try {
                const existing = get().executions.get(definition.id);
                if (
                    !_options?.actionParams &&
                    existing?.status === "running" &&
                    existing.actionName === actionName
                ) {
                    return {
                        success: false,
                        message: `Execution already running: ${definition.id}`,
                    };
                }
                tracksPrimaryExecution = !(
                    existing?.status === "running" &&
                    !!existing.actionName &&
                    existing.actionName !== actionName
                );

                // Mark running immediately for UI feedback (clear previous error).
                // If another action (e.g. "compute") is already running, do NOT
                // overwrite its state — auxiliary actions (approve_permission,
                // answer_input, cancel) run concurrently and must not clobber
                // the primary action's running/progress state.
                set((s) => {
                    const next = new Map(s.executions);
                    const prev = next.get(definition.id);

                    if (
                        prev?.status === "running" &&
                        prev.actionName &&
                        prev.actionName !== actionName
                    ) {
                        // Another action is already running — keep its state intact
                        return {};
                    }

                    // Strip __error from previous output so UI doesn't show stale error during re-run
                    let cleanOutput = prev?.output ?? null;
                    if (cleanOutput?.__error) {
                        const { __error: _, ...rest } = cleanOutput;
                        cleanOutput = rest as typeof cleanOutput;
                    }
                    next.set(definition.id, {
                        unitId: definition.id,
                        status: "running",
                        actionName,
                        result: null,
                        output: cleanOutput,
                        startedAt: Date.now(),
                        duration: null,
                    });
                    return { executions: next, executionError: null };
                });

                const { sessionId, clientConfig } =
                    await prepareExecutionRequest(get, undefined, lease);

                const started = await waitForEnginePromise(
                    engine.executionStart({
                        sessionId,
                        target: "unit",
                        unitId: definition.id,
                        action: actionName,
                        actionParams: _options?.actionParams,
                        clientConfig,
                    }),
                    lease,
                );
                lease.assertCurrent();
                if (tracksPrimaryExecution) {
                    trackedExecutionId = started.executionId;
                    setActiveExecutionId(definition.id, started.executionId);
                }
                const result = await executionJobWaiter.wait(
                    started.executionId,
                    lease,
                );
                lease.assertCurrent();

                if (result.status === "cancelled") {
                    return {
                        success: false,
                        message: result.message ?? "Execution cancelled by user",
                    };
                }

                if (result.success && (result.output !== undefined || _options?.actionParams)) {
                    await refreshDocumentBundleAfterExecution(sessionId, lease);
                }

                if (result.success && result.output !== undefined) {
                    lease.assertCurrent();
                    applyHydratedExecutionUnitOutput(
                        set,
                        definition.id,
                        result.output,
                        { mode: "always" },
                    );
                }

                if (result.success && _options?.actionParams) {
                    const mutatedOutputUnitIds = [
                        ...(result.mutatedOutputUnitIds ?? []),
                        ...(result.clearedOutputUnitIds ?? []),
                    ].filter((unitId) => unitId !== definition.id);
                    if (mutatedOutputUnitIds.length > 0) {
                        await Promise.all(
                            [...new Set(mutatedOutputUnitIds)].map((unitId) =>
                                hydrateUnitOutputFromSessionState(
                                    set,
                                    sessionId,
                                    unitId,
                                    { mode: "always" },
                                    lease,
                                ),
                            ),
                        );
                        lease.assertCurrent();
                    }
                    else if (
                        result.mutatedOutputUnitIds === undefined &&
                        result.clearedOutputUnitIds === undefined
                    ) {
                        // Compatibility with older Engine versions that do not report
                        // the mutation scope yet.
                        await hydrateAllSessionOutputs(set, sessionId, {
                            mode: "always",
                        }, {}, lease);
                    }
                }

                if (!result.success) {
                    const failureMessage =
                        result.message ?? result.error ?? "Unknown error";
                    const existingResult = get().executions.get(definition.id)?.result;
                    const existingMessage =
                        existingResult && existingResult.success === false
                            ? existingResult.message
                            : undefined;
                    if (existingMessage !== failureMessage) {
                        log.error(
                            `Execution failed: ${definition.id} — ${failureMessage}`,
                            {
                                source: "engine",
                                unitId: definition.id,
                                pluginName: definition.pluginName,
                            },
                        );
                    }
                }

                // The Engine Server sends notifications during execution that update
                // output and document content. The RPC response only has success/message.
                // We update the final status here -- output was already set by the
                // execution.completed notification handler.
                const currentFinal = get().executions.get(definition.id);
                const finalSuccess = result.success === true;
                const alreadyFinal =
                    currentFinal?.status === (finalSuccess ? "success" : "error") &&
                    currentFinal.actionName === actionName;

                if (!alreadyFinal) {
                    set((s) => {
                        const next = new Map(s.executions);
                        const prev = next.get(definition.id);

                        // If the primary action is still running and a different
                        // (auxiliary) action just completed, do NOT transition to
                        // success/error — keep the running state so progress events
                        // from the primary action continue to be processed.
                        if (
                            prev?.status === "running" &&
                            prev.actionName &&
                            prev.actionName !== actionName
                        ) {
                            // Only update output if the RPC provided one
                            if (prev.output) {
                                next.set(definition.id, { ...prev, output: prev.output });
                                return { executions: next };
                            }
                            return {};
                        }

                        const hasCycleError =
                            !finalSuccess &&
                            (result.message?.includes(CIRCULAR_DEPENDENCY_TEXT) ||
                                result.error?.includes(CIRCULAR_DEPENDENCY_TEXT));
                        const message = hasCycleError
                            ? circularDependencyMessage()
                            : result.message;
                        const startedAt = prev?.startedAt ?? Date.now();
                        next.set(definition.id, {
                            unitId: definition.id,
                            status: finalSuccess ? "success" : "error",
                            actionName,
                            result: {
                                success: finalSuccess,
                                message,
                                output: prev?.output ?? undefined,
                            },
                            output: prev?.output ?? null,
                            startedAt,
                            duration: Date.now() - startedAt,
                        });

                        return {
                            executions: next,
                            ...(hasCycleError
                                ? { executionError: message }
                                : {}),
                        };
                    });
                }

                const currentOutput = get().executions.get(definition.id)?.output;
                if (!currentOutput && result.success && sessionId) {
                    await hydrateUnitOutputFallbackIfNeeded(
                        set,
                        sessionId,
                        definition.id,
                        { mode: "always" },
                        lease,
                    );
                    lease.assertCurrent();
                }

                const returnMessage =
                    !finalSuccess &&
                        (result.message?.includes(CIRCULAR_DEPENDENCY_TEXT) ||
                            result.error?.includes(CIRCULAR_DEPENDENCY_TEXT))
                        ? circularDependencyMessage()
                        : result.message ?? result.error;

                return {
                    success: !!result.success,
                    message: returnMessage,
                    output: get().executions.get(definition.id)?.output ?? undefined,
                };
            } catch (err) {
                if (isStaleEngineOperationError(err)) {
                    return {
                        success: false,
                        message: ENGINE_SWITCHED_DURING_EXECUTION_MESSAGE,
                    };
                }
                const errMsg = err instanceof Error ? err.message : String(err);
                log.error(`Execution failed for ${definition.id}: ${errMsg}`, {
                    source: "engine",
                    unitId: definition.id,
                    pluginName: definition.pluginName,
                });

                set((s) => {
                    const next = new Map(s.executions);
                    const prev = next.get(definition.id);

                    // If another action is still running, don't transition to error
                    if (
                        prev?.status === "running" &&
                        prev.actionName &&
                        prev.actionName !== actionName
                    ) {
                        return { executions: next };
                    }

                    const hasCycleError = errMsg.includes(CIRCULAR_DEPENDENCY_TEXT);
                    const message = hasCycleError ? circularDependencyMessage() : errMsg;
                    next.set(definition.id, {
                        unitId: definition.id,
                        status: "error",
                        actionName,
                        result: { success: false, message },
                        output: prev?.output ?? null,
                        startedAt: prev?.startedAt ?? Date.now(),
                        duration: Date.now() - (prev?.startedAt ?? Date.now()),
                    });
                    return {
                        executions: next,
                        ...(hasCycleError ? { executionError: message } : {}),
                    };
                });

                return {
                    success: false,
                    message: errMsg.includes(CIRCULAR_DEPENDENCY_TEXT)
                        ? circularDependencyMessage()
                        : errMsg,
                };
            } finally {
                if (trackedExecutionId) {
                    clearActiveExecutionId(
                        definition.id,
                        trackedExecutionId,
                    );
                }
            }
            };

            if (shouldQueueRendererComputeAction(definition, actionName, _options)) {
                return enqueueRendererComputeAction(executeAction);
            }

            return executeAction();
        },

        runPrimary: async (definition, definitions, options) => {
            // Ensure engine is initialized so enginePlugins is populated
            // before looking up the primary action name.
            if (!get().engineReady) {
                await get()._initEngine();
            }
            const actionName = get()._getPrimaryAction(definition.pluginName);
            if (!actionName) return null;
            return get().runAction(definition, actionName, definitions, options);
        },

        runAll: async (_definitions) => {
            const tabId = getFocusedOrActiveTabId();
            await get().runAllForTab(tabId);
        },

        runAllForTab: async (tabId) => {
            if (tabId && get().runAllPendingTabIds.has(tabId)) return;
            setRunAllPendingTab(set, tabId, true);
            const log = useLogStore.getState();
            const engine = getPlatformService().engine;
            const lease = engineOperationController.capture();

            try {
                const { sessionId, clientConfig } =
                    await prepareExecutionRequest(get, tabId, lease);

                if (isRunAllRunningForSession(get(), sessionId)) {
                    setRunAllPendingTab(set, tabId, false);
                    return;
                }

                if (
                    (!tabId || tabId === getFocusedOrActiveTabId()) &&
                    get().sessionId !== sessionId
                ) {
                    set({ sessionId });
                }

                updateRunAllSessionState(
                    set,
                    sessionId,
                    {
                        isRunning: true,
                        progress: { current: 0, total: 0, currentId: null },
                    },
                    null,
                );
                if (get().sessionId === sessionId) {
                    set({ executionError: null });
                }

                const started = await waitForEnginePromise(
                    engine.executionStart({
                        sessionId,
                        target: "all",
                        clientConfig,
                    }),
                    lease,
                );
                lease.assertCurrent();
                updateRunAllSessionState(
                    set,
                    sessionId,
                    {
                        isRunning: true,
                        progress: { current: 0, total: 0, currentId: null },
                    },
                    started.executionId,
                );
                const result = await executionJobWaiter.wait(
                    started.executionId,
                    lease,
                );
                lease.assertCurrent();

                if (result.status === "cancelled") {
                    setRunAllPendingTab(set, tabId, false);
                    updateRunAllSessionState(set, sessionId, null, null);
                    log.info(`Run All cancelled by user: ${tabId}`, {
                        source: "system",
                    });
                    return;
                }

                if (result.status === "failed") {
                    throw new Error(result.error ?? "Run All failed");
                }

                if (result.success) {
                    await refreshDocumentBundleAfterExecution(sessionId, lease);
                }

                await hydrateRunAllOutputsFallbackIfNeeded(
                    set,
                    sessionId,
                    lease,
                );
                lease.assertCurrent();

                setRunAllPendingTab(set, tabId, false);
                updateRunAllSessionState(set, sessionId, null, null);

                log.info(
                    `Run All completed for ${tabId}: ${result.executed ?? 0} executed, ${result.failed ?? 0} failed`,
                    { source: "engine" },
                );
            } catch (err) {
                if (isStaleEngineOperationError(err)) return;
                const errMsg = err instanceof Error ? err.message : String(err);
                log.error(`Run All failed for ${tabId}: ${errMsg}`, { source: "engine" });
                const sessionId =
                    tabSessionRegistry.getSessionForTab(tabId) ??
                    (!tabId || tabId === getFocusedOrActiveTabId()
                        ? get().sessionId
                        : null);
                if (sessionId) {
                    updateRunAllSessionState(set, sessionId, null, null);
                }
                setRunAllPendingTab(set, tabId, false);
                if (tabId === getFocusedOrActiveTabId()) {
                    set({ executionError: errMsg });
                }
            }
        },

        cancelRunAll: () => {
            const tabId = getFocusedOrActiveTabId();
            if (tabId && tabSessionRegistry.getSessionForTab(tabId)) {
                get().cancelRunAllForTab(tabId);
                return;
            }
            const { activeRunAllExecutionId } = get();
            if (activeRunAllExecutionId) {
                getPlatformService()
                    .engine.executionCancel({ executionId: activeRunAllExecutionId })
                    .catch(() => {});
            }
            set(buildCancelActiveRunAllPatch);
        },

        cancelRunAllForTab: (tabId) => {
            const sessionId = tabSessionRegistry.getSessionForTab(tabId);
            if (!sessionId) return;
            setRunAllPendingTab(set, tabId, false);

            const currentState = get();
            const snapshot =
                currentState.sessionId === sessionId
                    ? saveActiveToSnapshot(currentState)
                    : currentState.sessionExecutionCache.get(sessionId);
            const activeRunAllExecutionId =
                currentState.sessionId === sessionId
                    ? currentState.activeRunAllExecutionId
                    : (snapshot?.activeRunAllExecutionId ?? null);
            if (activeRunAllExecutionId) {
                const engine = getPlatformService().engine;
                engine.executionCancel({ executionId: activeRunAllExecutionId }).catch((err) => {
                    useLogStore
                        .getState()
                        .warn(
                            `Cancel failed: ${err instanceof Error ? err.message : String(err)}`,
                            { source: "engine" },
                        );
                });
            }

            // Optimistically reset running entries to idle
            if (currentState.sessionId !== sessionId) {
                get().cancelBackgroundTabExecutions(tabId);
                return;
            }

            set(buildCancelActiveRunAllPatch);
            updateRunAllSessionState(set, sessionId, null, null);

            useLogStore
                .getState()
                .info(`Run All cancelled by user: ${tabId}`, { source: "system" });
        },

        cancelExecution: (id) => {
            const current = get();
            const executionId = current.activeExecutionIdsByUnit.get(id);
            const isRunningInRunAll =
                current.isRunningAll &&
                !!current.activeRunAllExecutionId &&
                (
                    current.executions.get(id)?.status === "running" ||
                    current.forExecutions.get(id)?.status === "running" ||
                    current.contentExecutions.get(id)?.status === "running" ||
                    current.importExecutions.get(id)?.status === "running" ||
                    current.useExecutions.get(id)?.status === "running"
                );
            if (!executionId && isRunningInRunAll) {
                get().cancelRunAll();
                return;
            }
            if (executionId) {
                const engine = getPlatformService().engine;
                engine.executionCancel({ executionId }).catch(() => { });
            }

            set((s) => {
                const updates: Partial<PluginExecutionState> = {};

                // Check definition executions
                const defPrev = s.executions.get(id);
                if (defPrev && defPrev.status === "running") {
                    const next = new Map(s.executions);
                    next.set(id, {
                        ...defPrev,
                        status: "idle",
                        duration: defPrev.startedAt ? Date.now() - defPrev.startedAt : null,
                    });
                    updates.executions = next;
                }

                if (s.activeExecutionIdsByUnit.has(id)) {
                    const next = new Map(s.activeExecutionIdsByUnit);
                    next.delete(id);
                    updates.activeExecutionIdsByUnit = next;
                }

                // Check for-block executions
                const forPrev = s.forExecutions.get(id);
                if (forPrev && forPrev.status === "running") {
                    const next = new Map(s.forExecutions);
                    next.set(id, {
                        ...forPrev,
                        status: "idle",
                        duration: forPrev.startedAt ? Date.now() - forPrev.startedAt : null,
                    });
                    updates.forExecutions = next;
                }

                // Check content executions
                const contentPrev = s.contentExecutions.get(id);
                if (contentPrev && contentPrev.status === "running") {
                    const next = new Map(s.contentExecutions);
                    next.set(id, {
                        ...contentPrev,
                        status: "idle",
                        duration: contentPrev.startedAt
                            ? Date.now() - contentPrev.startedAt
                            : null,
                    });
                    updates.contentExecutions = next;
                }

                // Check import executions
                const impPrev = s.importExecutions.get(id);
                if (impPrev && impPrev.status === "running") {
                    const next = new Map(s.importExecutions);
                    next.set(id, {
                        ...impPrev,
                        status: "idle",
                        duration: impPrev.startedAt ? Date.now() - impPrev.startedAt : null,
                    });
                    updates.importExecutions = next;
                }

                // Check use executions
                const usePrev = s.useExecutions.get(id);
                if (usePrev && usePrev.status === "running") {
                    const next = new Map(s.useExecutions);
                    next.set(id, {
                        ...usePrev,
                        status: "idle",
                        duration: usePrev.startedAt ? Date.now() - usePrev.startedAt : null,
                    });
                    updates.useExecutions = next;
                }

                return updates;
            });

            useLogStore.getState().info(`Cancelled execution for ${id}`, {
                source: "engine",
                unitId: id,
            });
        },

        clearExecution: (id) => {
            set((s) => {
                const nextExecutions = new Map(s.executions);
                const nextForExecutions = new Map(s.forExecutions);
                const nextContentExecutions = new Map(s.contentExecutions);
                const nextImportExecutions = new Map(s.importExecutions);
                const nextUseExecutions = new Map(s.useExecutions);

                nextExecutions.delete(id);
                nextForExecutions.delete(id);
                nextContentExecutions.delete(id);
                nextImportExecutions.delete(id);
                nextUseExecutions.delete(id);

                const patch: Partial<PluginExecutionState> = {
                    executions: nextExecutions,
                    forExecutions: nextForExecutions,
                    contentExecutions: nextContentExecutions,
                    importExecutions: nextImportExecutions,
                    useExecutions: nextUseExecutions,
                };

                if (s.sessionId) {
                    const snapshot = s.sessionExecutionCache.get(s.sessionId);
                    if (snapshot) {
                        const nextCache = new Map(s.sessionExecutionCache);
                        nextCache.set(s.sessionId, {
                            ...snapshot,
                            executions: nextExecutions,
                            forExecutions: nextForExecutions,
                            contentExecutions: nextContentExecutions,
                            importExecutions: nextImportExecutions,
                            useExecutions: nextUseExecutions,
                        });
                        patch.sessionExecutionCache = nextCache;
                    }
                }

                const nextActiveExecutionIds = new Map(s.activeExecutionIdsByUnit);
                nextActiveExecutionIds.delete(id);
                patch.activeExecutionIdsByUnit = nextActiveExecutionIds;

                return patch;
            });
        },

        clearAll: () => {
            set({
                executions: new Map(),
                forExecutions: new Map(),
                contentExecutions: new Map(),
                importExecutions: new Map(),
                useExecutions: new Map(),
                activeRunAllExecutionId: null,
                activeExecutionIdsByUnit: new Map(),
                executionError: null,
            });
        },

        restoreFromParsedOutput: (
            definitions,
            forDefinitions,
            uses,
            imports,
            contentDefinitions,
        ) => {
            const patch = createParsedOutputExecutionPatch(get(), {
                definitions,
                forDefinitions,
                uses,
                imports,
                contentDefinitions,
            });
            if (Object.keys(patch).length > 0) set(patch);
        },

        /* -- Engine Lifecycle -- */

        _initEngine: async () => {
            if (get().engineReady) return;

            const log = useLogStore.getState();
            const engine = getPlatformService().engine;
            return engineInitController.run(async (lease) => {
                try {
                    log.info("Initializing Engine Server connection...", {
                        source: "engine",
                    });

                    const initResult = await engine.initialize({
                        name: "magic-md-editor",
                        version: "0.1.0",
                    });
                    if (!lease.isCurrent()) return;

                    // Register routing before opening the transport so early
                    // notifications cannot fall into the initialize/connect gap.
                    const bus = getNotificationBus();
                    if (notificationHandlersBus !== bus) {
                        disposeNotificationHandlers?.();
                        disposeNotificationHandlers =
                            registerNotificationHandlers(
                                bus,
                                usePluginExecutionStore,
                                pluginExecutionNotificationHandlerDependencies,
                            );
                        notificationHandlersBus = bus;
                    }
                    await bus.connect(engine);
                    if (!lease.isCurrent()) return;

                    await useEditorStore
                        .getState()
                        .hydrateWorkspaceFromEngine();
                    if (!lease.isCurrent()) return;

                    // Engine readiness includes the notification channel. A
                    // successful initialize RPC without events is not a usable
                    // thin-client connection and must remain retryable.
                    set({
                        engineReady: true,
                        enginePlugins: initResult.plugins,
                    });

                    log.info(
                        `Engine Server connected: ${initResult.serverInfo.name} v${initResult.serverInfo.version}, ` +
                        `${initResult.plugins.length} plugins loaded`,
                        { source: "engine" },
                    );

                    log.info("Engine notification listener started", {
                        source: "engine",
                    });
                } catch (err) {
                    if (!lease.isCurrent()) return;
                    const errMsg = err instanceof Error ? err.message : String(err);
                    set({ engineReady: false });
                    log.error(`Engine initialization failed: ${errMsg}`, {
                        source: "engine",
                    });
                }
            });
        },

        _ensureSession: async (tabId?: string) => {
            // ⚠️ Capture the tab ID that initiated this request.
            // activeTabId may change during async operations (tab switch),
            // so we must use the captured ID consistently throughout.
            const capturedTabId = tabId ?? getActiveTabId() ?? "";
            const existingSessionId = getTabSessionId(capturedTabId);
            if (existingSessionId) return existingSessionId;

            const inflightSession = tabSessionCreationController.get(capturedTabId);
            if (inflightSession) {
                frontendLog.debug(
                    `[session] _ensureSession: tab=${capturedTabId} reusing inflight promise`,
                    undefined,
                    { source: "engine" },
                );
                return inflightSession;
            }

            frontendLog.debug(
                `[session] _ensureSession: tab=${capturedTabId} creating new session`,
                undefined,
                { source: "engine" },
            );

            return tabSessionCreationController.run(capturedTabId, async (lease) => {
                // Make sure engine is initialized first
                if (!get().engineReady) {
                    await get()._initEngine();
                }

                if (!lease.isCurrent()) {
                    throw new Error("Session creation was superseded");
                }

                // Use captured tab ID — getActiveTab() may return a different tab
                // if the user switched tabs during _initEngine().
                let tab = useEditorStore
                    .getState()
                    .tabs.find((t) => t.id === capturedTabId);
                if (!tab) {
                    const errMsg = "No active document — open or select a file first";
                    useLogStore.getState().error(errMsg, { source: "engine" });
                    throw new Error(errMsg);
                }

                // Wait for the tab's file content to finish loading. Otherwise we
                // would call sessionCreate with empty content and Engine would
                // create a session bound to an empty document — losing all
                // hydrated external outputs and showing "not executed" forever.
                if (tab.isLoading) {
                    await new Promise<void>((resolve) => {
                        const unsub = useEditorStore.subscribe((state) => {
                            const t = state.tabs.find((x) => x.id === capturedTabId);
                            if (!t || !t.isLoading) {
                                unsub();
                                resolve();
                            }
                        });
                        // Re-check synchronously in case it already finished.
                        const t = useEditorStore
                            .getState()
                            .tabs.find((x) => x.id === capturedTabId);
                        if (!t || !t.isLoading) {
                            unsub();
                            resolve();
                        }
                    });
                    tab = useEditorStore
                        .getState()
                        .tabs.find((t) => t.id === capturedTabId);
                    if (!tab) {
                        const errMsg = "Tab closed before session creation";
                        useLogStore.getState().error(errMsg, { source: "engine" });
                        throw new Error(errMsg);
                    }
                }

                if (!lease.isCurrent()) {
                    throw new Error("Session creation was superseded");
                }

                const engine = getPlatformService().engine;
                const requestedFilePath = tab.filePath;

                try {
                    const result = await engine.sessionCreate({
                        filePath: tab.filePath,
                        content: tab.content,
                        includeOutputs: shouldHydrateSessionOutputsForContent(tab.content),
                        includePresentationContent: false,
                    });

                    const currentTab = useEditorStore
                        .getState()
                        .tabs.find((candidate) => candidate.id === capturedTabId);
                    if (
                        !lease.isCurrent() ||
                        !currentTab ||
                        currentTab.filePath !== requestedFilePath
                    ) {
                        frontendLog.warn(
                            `[session] _ensureSession: tab=${capturedTabId} changed before session ready — closing orphaned session=${result.sessionId}`,
                            undefined,
                            { source: "engine" },
                        );
                        engine.sessionClose(result.sessionId).catch(() => { });
                        throw new Error(
                            "Session target tab changed before it became ready",
                        );
                    }

                    // Always persist session→tab mapping immediately so that
                    // event routing (execution, document.changed) can find
                    // the owning tab even if the user has switched away.
                    // The registry's atomic link clears any orphan reverse
                    // mapping if `capturedTabId` was previously bound to a
                    // different session, or if `result.sessionId` was
                    // previously bound to a different tab.
                    tabSessionRegistry.link({
                        tabId: capturedTabId,
                        sessionId: result.sessionId,
                        filePath: tab.filePath,
                    });
                    sessionRevisionTracker.record(
                        result.sessionId,
                        result.revision,
                    );

                    // If the Engine returned authoritative content (reused
                    // session with different content), reconcile the tab now
                    // so we don't leave stale content visible.
                    // Editor content should prefer persistence content so
                    // output=external remains represented as stubs in CM.
                    reconcileEngineTabContent({
                        tabId: capturedTabId,
                        sessionId: result.sessionId,
                        tabTitle: tab.title,
                        logPrefix: "session",
                        filePath: tab.filePath,
                        fallbackContent: tab.content,
                        content: result.content,
                        presentationContent: result.presentationContent,
                    });

                    // Only set as the global (foreground) sessionId if the
                    // captured tab is still the active tab. Otherwise the
                    // session belongs to a background tab — save to map only.
                    const currentActiveTabId = getActiveTabId() ?? "";

                    // Extract Engine-resolved definitions from units
                    // (includes imported ns/id symbols not in client-side parser output)
                    const { engineDefinitions, engineContentDefinitions, engineForBlocks } =
                        buildEngineUnitMaps(result.units);

                    // Hydrate pre-existing outputs from the Engine so the
                    // frontend can resolve $ref and render previews without
                    // requiring a re-run.
                    // For background tabs, base on session cache (not active state)
                    // to avoid cross-tab contamination.
                    const isBackgroundTab = capturedTabId !== currentActiveTabId;
                    const existingSnapshot = isBackgroundTab
                        ? (get().sessionExecutionCache.get(result.sessionId) ?? createEmptySnapshot())
                        : undefined;
                    const hydratedMaps = createHydratedExecutionMaps(
                        isBackgroundTab ? existingSnapshot! : get(),
                        result.outputs,
                        { mode: "missing" },
                    );

                    const hasHydratedOutputs = result.outputs && Object.keys(result.outputs).length > 0;

                    if (currentActiveTabId === capturedTabId) {
                        frontendLog.debug(
                            `[session] _ensureSession: tab=${capturedTabId} mapped → session=${result.sessionId} (global + map)${hasHydratedOutputs ? ` with ${Object.keys(result.outputs!).length} hydrated outputs` : ""}`,
                            undefined,
                            { source: "engine" },
                        );
                        set({
                            sessionId: result.sessionId,
                            engineDefinitions,
                            engineContentDefinitions,
                            engineForBlocks,
                            ...(hasHydratedOutputs ? {
                                executions: hydratedMaps.executions,
                                forExecutions: hydratedMaps.forExecutions,
                                contentExecutions: hydratedMaps.contentExecutions,
                                importExecutions: hydratedMaps.importExecutions,
                                useExecutions: hydratedMaps.useExecutions,
                            } : {}),
                        });
                    } else {
                        frontendLog.debug(
                            `[session] _ensureSession: tab=${capturedTabId} mapped → session=${result.sessionId} (map-only, active=${currentActiveTabId})${hasHydratedOutputs ? ` with ${Object.keys(result.outputs!).length} hydrated outputs` : ""}`,
                            undefined,
                            { source: "engine" },
                        );
                        // For background tabs, save hydrated executions to session cache
                        // so they're restored when the user switches to this tab.
                        const nextCache = new Map(get().sessionExecutionCache);
                        const snapshot = getOrCreateSnapshot(nextCache, result.sessionId);
                        snapshot.engineDefinitions = engineDefinitions;
                        snapshot.engineContentDefinitions = engineContentDefinitions;
                        snapshot.engineForBlocks = engineForBlocks;
                        if (hasHydratedOutputs) {
                            snapshot.executions = hydratedMaps.executions;
                            snapshot.forExecutions = hydratedMaps.forExecutions;
                            snapshot.contentExecutions = hydratedMaps.contentExecutions;
                            snapshot.importExecutions = hydratedMaps.importExecutions;
                            snapshot.useExecutions = hydratedMaps.useExecutions;
                        }
                        nextCache.set(result.sessionId, snapshot);
                        set({
                            sessionExecutionCache: nextCache,
                        } as Partial<ReturnType<typeof get>>);
                    }

                    // Start watching the session's file for external changes
                    if (tab.filePath) {
                        engine
                            .sessionWatch({ sessionId: result.sessionId })
                            .catch((err) => {
                                frontendLog.warn(
                                    `[session] Failed to start file watch: ${err}`,
                                    undefined,
                                    { source: "engine" },
                                );
                            });
                    }

                    useLogStore
                        .getState()
                        .info(
                            `Engine session created: ${result.sessionId} (${result.units.length} definitions)`,
                            { source: "engine" },
                        );
                    return result.sessionId;
                } catch (err) {
                    const errMsg = err instanceof Error ? err.message : String(err);
                    useLogStore
                        .getState()
                        .error(`Failed to create engine session: ${errMsg}`, {
                            source: "engine",
                        });
                    throw new Error(`Session creation failed: ${errMsg}`);
                }
            });
        },

        _handleNotification: (notification) => {
            // Delegate to the NotificationBus. This method is kept for backward
            // compatibility — callers that used to invoke _handleNotification
            // directly (e.g. tests) continue to work.
            getNotificationBus().dispatch(notification);
        },
        _getPrimaryAction: (pluginName: string): string | null => {
            const plugin = get().enginePlugins.find((p) => p.name === pluginName);
            if (!plugin) return "compute"; // fallback — most plugins use "compute"

            const primary = plugin.actions.find((a) => a.primary);
            if (primary) return primary.name;

            // Prefer "run" over "compute" as default action name
            if (plugin.actions.some((a) => a.name === "run")) return "run";
            if (plugin.actions.some((a) => a.name === "compute")) return "compute";

            return plugin.actions[0]?.name ?? null;
        },

        cancelBackgroundTabExecutions: (tabId: string) => {
            const { sessionExecutionCache } = get();
            const sessionId = tabSessionRegistry.getSessionForTab(tabId);
            if (sessionId) {
                // Reset running entries in session cache to idle
                const snapshot = sessionExecutionCache.get(sessionId);
                if (snapshot) {
                    const engine = getPlatformService().engine;
                    const executionIds = new Set<string>();
                    if (snapshot.activeRunAllExecutionId) {
                        executionIds.add(snapshot.activeRunAllExecutionId);
                    }
                    for (const executionId of snapshot.activeExecutionIdsByUnit.values()) {
                        executionIds.add(executionId);
                    }
                    for (const executionId of executionIds) {
                        engine.executionCancel({ executionId }).catch(() => { });
                    }

                    const nextCache = new Map(sessionExecutionCache);
                    const nextSnapshot = getOrCreateSnapshot(nextCache, sessionId);
                    nextSnapshot.activeRunAllExecutionId = null;
                    nextSnapshot.activeExecutionIdsByUnit = new Map();
                    for (const [id, entry] of nextSnapshot.executions) {
                        if (entry.status === "running") {
                            nextSnapshot.executions.set(id, {
                                ...entry,
                                status: "idle",
                                duration: entry.startedAt ? Date.now() - entry.startedAt : null,
                            });
                        }
                    }
                    for (const [id, entry] of nextSnapshot.forExecutions) {
                        if (entry.status === "running") {
                            nextSnapshot.forExecutions.set(id, { ...entry, status: "idle" as ForExecutionStatus });
                        }
                    }
                    for (const [id, entry] of nextSnapshot.contentExecutions) {
                        if (entry.status === "running") {
                            nextSnapshot.contentExecutions.set(id, { ...entry, status: "idle" as ContentExecutionStatus });
                        }
                    }
                    for (const [id, entry] of nextSnapshot.importExecutions) {
                        if (entry.status === "running") {
                            nextSnapshot.importExecutions.set(id, { ...entry, status: "idle" as ImportExecutionStatus });
                        }
                    }
                    for (const [id, entry] of nextSnapshot.useExecutions) {
                        if (entry.status === "running") {
                            nextSnapshot.useExecutions.set(id, { ...entry, status: "idle" as UseExecutionStatus });
                        }
                    }
                    if (nextSnapshot.runAllState?.isRunning) {
                        nextSnapshot.runAllState = { isRunning: false, progress: null };
                    }
                    nextCache.set(sessionId, nextSnapshot);
                    set({ sessionExecutionCache: nextCache });
                }
            }

            _syncBackgroundRunningTabs();

            useLogStore
                .getState()
                .info(`Cancelled background executions for tab ${tabId}`, {
                    source: "engine",
                });
        },

        resetForEngineSwitch: () => {
            const { sessionId } = get();
            engineOperationController.invalidate();
            lazyOutputHydrationPromises.clear();
            sessionRevisionTracker.clear();

            // Attempt to close the current session (best effort, engine may already be gone)
            if (sessionId) {
                const engine = getPlatformService().engine;
                engine.sessionClose(sessionId).catch(() => { });
            }

            // Close all cached tab sessions
            const engine = getPlatformService().engine;
            for (const link of tabSessionRegistry.entries()) {
                if (link.sessionId !== sessionId) {
                    engine.sessionClose(link.sessionId).catch(() => { });
                }
            }
            tabSessionRegistry.clear();

            // Clear all per-tab sync timers
            for (const timer of tabSyncTimerMap.values()) {
                if (timer) clearTimeout(timer);
            }
            if (autoSessionTimer) {
                clearTimeout(autoSessionTimer);
                autoSessionTimer = null;
            }
            tabSyncTimerMap.clear();
            tabPrevContentMap.clear();
            tabSessionCreationController.clear();
            tabSessionSyncTracker.clear();
            engineInitController.invalidate();
            clientExecutionConfigCache.reset();
            resetRendererComputeActionQueue();
            disposeNotificationHandlers?.();
            disposeNotificationHandlers = null;
            notificationHandlersBus = null;
            resetNotificationBus();
            resetEngineOverviewStore();

            // Reset all execution & session state (including session execution cache)
            set({
                executions: new Map(),
                isRunningAll: false,
                runAllProgress: null,
                activeRunAllExecutionId: null,
                activeExecutionIdsByUnit: new Map(),
                sessionId: null,
                executionError: null,
                engineReady: false,
                enginePlugins: [],
                engineDefinitions: new Map(),
                engineContentDefinitions: new Map(),
                engineForBlocks: new Map(),
                backgroundRunningTabs: new Set<string>(),
                runAllPendingTabIds: new Set<string>(),
                tabSessionMap: new Map(),
                sessionExecutionCache: new Map(),
                tabSyncVersionMap: new Map(),
                visibleTabIds: new Set(),
                forExecutions: new Map(),
                contentExecutions: new Map(),
                importExecutions: new Map(),
                useExecutions: new Map(),
            });

            useLogStore.getState().info("Engine switched — execution state reset", {
                source: "engine",
            });
        },
    }),
);


/* -- Session lifecycle: manage session & executions on tab switch -- */
//
// ⚠️ This subscriber MUST be registered BEFORE the "auto-restore from parsed
// output" subscriber below. Both fire synchronously from the same
// setActiveTab() call.  We need to save/restore per-tab execution state
// before restoreFromParsedOutput() runs (which would otherwise delete
// execution entries belonging to the previous tab).

let prevActiveTabId: string | null = null;
let autoSessionTimer: ReturnType<typeof setTimeout> | null = null;

/** Per-tab sync timers (Phase 1 Multi-Pane: replaces single syncDebounceTimer for per-tab sync) */
const tabSyncTimerMap = new Map<string, ReturnType<typeof setTimeout> | null>();
/** Per-tab previous content (Phase 1 Multi-Pane: replaces single prevContent) */
const tabPrevContentMap = new Map<string, string | null>();
/**
 * Tracks the last-known filePath for each tab so we can detect Save As /
 * rename operations and invalidate the stale session mapping.
 */
const tabFilePathSnapshot = new Map<string, string | undefined>();

function haveTabLifecycleInputsChanged(
    tabs: EditorTab[],
    prevTabs: EditorTab[],
): boolean {
    if (tabs.length !== prevTabs.length) return true;
    for (let i = 0; i < tabs.length; i++) {
        const tab = tabs[i];
        const prevTab = prevTabs[i];
        if (!prevTab || tab.id !== prevTab.id || tab.filePath !== prevTab.filePath) {
            return true;
        }
    }
    return false;
}

function tabHasExecutableSyntax(tabId: string): boolean {
    const editorState = useEditorStore.getState();
    const parsed =
        editorState.parsedDocumentMap.get(tabId) ??
        (getActiveTabId() === tabId ? editorState.parsedDocument : null);
    if (!parsed) return false;
    return (
        parsed.definitions.size > 0 ||
        (parsed.forDefinitions?.size ?? 0) > 0 ||
        (parsed.uses?.length ?? 0) > 0 ||
        (parsed.imports?.length ?? 0) > 0 ||
        (parsed.contentDefinitions?.size ?? 0) > 0
    );
}

useEditorStore.subscribe((state, prev) => {
    const activeTabChanged = state.activeTabId !== prev.activeTabId;
    const tabLifecycleInputsChanged =
        state.tabs !== prev.tabs &&
        haveTabLifecycleInputsChanged(state.tabs, prev.tabs);

    if (!activeTabChanged && !tabLifecycleInputsChanged) {
        return;
    }

    const { activeTabId, tabs } = state;

    if (tabLifecycleInputsChanged) {
        const { tabSyncVersionMap } = usePluginExecutionStore.getState();
        const openTabIds = new Set(tabs.map((tab) => tab.id));

        // ── Handle tab removal: close sessions & clean cached state for tabs that no longer exist ──
        let cacheChanged = false;
        let syncVersionChanged = false;
        const nextCache = new Map(usePluginExecutionStore.getState().sessionExecutionCache);
        const nextSyncVersions = new Map(tabSyncVersionMap);

        // Pending creations may not have a registry link or sync timer yet.
        // Revoke them directly from the previous tab snapshot on removal.
        for (const previousTab of prev.tabs) {
            if (!openTabIds.has(previousTab.id)) {
                tabSessionCreationController.invalidate(previousTab.id);
            }
        }

        // Snapshot the registry once so iteration is not disturbed by unlinkTab.
        for (const { tabId, sessionId } of [...tabSessionRegistry.entries()]) {
            if (!openTabIds.has(tabId)) {
                // Skip engine session close if this tab is migrating to another window —
                // the destination window will reclaim the session by filePath.
                if (isTabMigrating(tabId)) {
                    frontendLog.debug(
                        `[tab-cleanup] Tab=${tabId} is migrating — keeping session=${sessionId} alive`,
                        undefined,
                        { source: "engine" },
                    );
                    tabSessionRegistry.unlinkTab(tabId);
                } else {
                    tabSessionRegistry.unlinkTab(tabId);
                    // Only close session when this is the last tab using it
                    const remainingTabsForSession = countTabsForSession(sessionId);
                    if (remainingTabsForSession === 0) {
                        if (pinnedUnitSessionRegistry.hasSessionLease(sessionId)) {
                            frontendLog.debug(
                                `[tab-cleanup] Keeping session=${sessionId} alive for pinned unit(s) after removed tab=${tabId}`,
                                undefined,
                                { source: "engine" },
                            );
                            continue;
                        }
                        frontendLog.debug(
                            `[tab-cleanup] Closing session=${sessionId} for removed tab=${tabId} (last tab)`,
                            undefined,
                            { source: "engine" },
                        );
                        const engine = getPlatformService().engine;
                        engine.sessionClose(sessionId).catch(() => { });
                        // Clean session cache entry
                        nextCache.delete(sessionId);
                        cacheChanged = true;
                    } else {
                        frontendLog.debug(
                            `[tab-cleanup] Tab=${tabId} removed — session=${sessionId} still has ${remainingTabsForSession} tab(s)`,
                            undefined,
                            { source: "engine" },
                        );
                    }
                }
            }
        }
        // Clean per-tab sync state for removed tabs
        for (const tabId of tabSyncTimerMap.keys()) {
            if (!openTabIds.has(tabId)) {
                const timer = tabSyncTimerMap.get(tabId);
                if (timer) clearTimeout(timer);
                tabSyncTimerMap.delete(tabId);
                tabPrevContentMap.delete(tabId);
                tabSessionCreationController.invalidate(tabId);
                tabSessionSyncTracker.delete(tabId);
                tabFilePathSnapshot.delete(tabId);
                if (nextSyncVersions.delete(tabId)) {
                    syncVersionChanged = true;
                }
            }
        }

        // ── Handle filePath changes (Save As / Rename): invalidate stale session mappings ──
        // When a tab's filePath changes, the engine session is still bound to the
        // old path.  We close the old session and remove the mapping so that the
        // next _ensureSession call creates a fresh session for the new path.
        for (const tab of tabs) {
            const hasPreviousPath = tabFilePathSnapshot.has(tab.id);
            const prevPath = tabFilePathSnapshot.get(tab.id);
            // Map presence distinguishes an unseen tab from a previously
            // untitled tab whose path was undefined before Save As.
            if (hasPreviousPath && prevPath !== tab.filePath) {
                const oldSessionId = tabSessionRegistry.getSessionForTab(tab.id);
                if (oldSessionId) {
                    tabSessionRegistry.unlinkTab(tab.id);
                    frontendLog.debug(
                        `[tab-rename] Closing stale session=${oldSessionId} for tab=${tab.id} (${prevPath} → ${tab.filePath})`,
                        undefined,
                        { source: "engine" },
                    );
                    const engine = getPlatformService().engine;
                    engine.sessionClose(oldSessionId).catch(() => { });
                    // Clean session cache if no other tabs reference this session
                    const remaining = countTabsForSession(oldSessionId);
                    if (remaining === 0) {
                        nextCache.delete(oldSessionId);
                        cacheChanged = true;
                    }
                }
                // Clear cached sync state for this tab — it belongs to the old session
                const timer = tabSyncTimerMap.get(tab.id);
                if (timer) clearTimeout(timer);
                tabSyncTimerMap.delete(tab.id);
                tabPrevContentMap.delete(tab.id);
                tabSessionCreationController.invalidate(tab.id);
                tabSessionSyncTracker.delete(tab.id);
                if (nextSyncVersions.delete(tab.id)) {
                    syncVersionChanged = true;
                }
            }
            // Update the snapshot for the next subscriber tick
            tabFilePathSnapshot.set(tab.id, tab.filePath);
        }
        // Clean snapshot entries for removed tabs
        for (const tabId of tabFilePathSnapshot.keys()) {
            if (!openTabIds.has(tabId)) {
                tabFilePathSnapshot.delete(tabId);
            }
        }

        if (cacheChanged || syncVersionChanged) {
            usePluginExecutionStore.setState({
                ...(cacheChanged ? { sessionExecutionCache: nextCache } : {}),
                ...(syncVersionChanged ? { tabSyncVersionMap: nextSyncVersions } : {}),
            });
        }
    }

    if (!activeTabChanged || activeTabId === prevActiveTabId) return;

    const prevTabId = prevActiveTabId;
    prevActiveTabId = activeTabId;

    frontendLog.debug(
        `[tab-switch] ${prevTabId ?? "(none)"} → ${activeTabId}`,
        undefined,
        { source: "system" },
    );

    // Cancel any pending auto-session creation
    if (autoSessionTimer) {
        clearTimeout(autoSessionTimer);
        autoSessionTimer = null;
    }

    // ── Save execution state for the previous tab into session cache ──
    const currentState = usePluginExecutionStore.getState();
    const updatedCache = new Map(currentState.sessionExecutionCache);

    const prevTabStillOpen = prevTabId
        ? tabs.some((tab) => tab.id === prevTabId)
        : false;

    if (prevTabId && prevTabStillOpen) {
        // Save sessionId — but ONLY if the global sessionId actually
        // belongs to prevTabId.  During rapid pane/focus switches the
        // global sessionId can already point to a different tab; blindly
        // persisting it would corrupt the tab→session mapping.
        if (currentState.sessionId) {
            const existingMapping = tabSessionRegistry.getSessionForTab(prevTabId);
            if (existingMapping === currentState.sessionId || !existingMapping) {
                const prevTab = useEditorStore
                    .getState()
                    .tabs.find((t) => t.id === prevTabId);
                tabSessionRegistry.link({
                    tabId: prevTabId,
                    sessionId: currentState.sessionId,
                    filePath: prevTab?.filePath,
                });
            }
        }

        // Save active execution state to session cache
        const prevSessionId = tabSessionRegistry.getSessionForTab(prevTabId);
        if (prevSessionId) {
            const snapshot = saveActiveToSnapshot(currentState);
            updatedCache.set(prevSessionId, snapshot);
        }
    }

    // ── Restore execution state for the new tab from session cache ──
    const cachedSessionId = tabSessionRegistry.getSessionForTab(activeTabId);
    const cachedSnapshot = cachedSessionId ? updatedCache.get(cachedSessionId) : undefined;
    const restored = cachedSnapshot ? snapshotToActiveState(cachedSnapshot) : undefined;

    if (cachedSessionId && restored) {
        usePluginExecutionStore.setState({
            sessionId: cachedSessionId,
            executionError: null,
            ...restored,
            sessionExecutionCache: updatedCache,
        });
    } else {
        // No cached session — clear global state to prevent cross-tab contamination
        usePluginExecutionStore.setState({
            sessionId: null,
            executionError: null,
            executions: new Map(),
            isRunningAll: false,
            runAllProgress: null,
            runAllPendingTabIds: new Set(),
            engineDefinitions: new Map(),
            engineContentDefinitions: new Map(),
            engineForBlocks: new Map(),
            forExecutions: new Map(),
            contentExecutions: new Map(),
            importExecutions: new Map(),
            useExecutions: new Map(),
            sessionExecutionCache: updatedCache,
        });
    }

    // Prewarm sessions only for documents that actually use executable syntax.
    const newTab = tabs.find((t) => t.id === activeTabId);
    if (
        newTab &&
        (newTab.filePath || newTab.content.trim()) &&
        tabHasExecutableSyntax(activeTabId)
    ) {
        autoSessionTimer = setTimeout(() => {
            autoSessionTimer = null;
            usePluginExecutionStore
                .getState()
                ._ensureSession()
                .catch((err) => {
                    console.warn("[magic-md] Auto-session prewarm failed:", err?.message ?? err);
                });
        }, 0);
    }

    // Recalculate background running tabs after save/restore
    _syncBackgroundRunningTabs();
});

/* -- Auto-restore from parsed output -- */

let prevParsedDocument: ReturnType<
    typeof useEditorStore.getState
>["parsedDocument"] = null;

useEditorStore.subscribe((state) => {
    const parsedDocument = state.parsedDocument;
    if (parsedDocument !== prevParsedDocument) {
        prevParsedDocument = parsedDocument;
        if (
            parsedDocument &&
            (parsedDocument.definitions.size > 0 ||
                (parsedDocument.forDefinitions &&
                    parsedDocument.forDefinitions.size > 0) ||
                (parsedDocument.uses && parsedDocument.uses.length > 0) ||
                (parsedDocument.imports && parsedDocument.imports.length > 0) ||
                (parsedDocument.contentDefinitions &&
                    parsedDocument.contentDefinitions.size > 0))
        ) {
            usePluginExecutionStore
                .getState()
                .restoreFromParsedOutput(
                    parsedDocument.definitions,
                    parsedDocument.forDefinitions,
                    parsedDocument.uses,
                    parsedDocument.imports,
                    parsedDocument.contentDefinitions,
                );

            // If the active tab just gained executable syntax (e.g. file content
            // loaded asynchronously in openFileFromPath) but we don't have a
            // session yet, trigger session creation.  This covers preview-only
            // mode where CM6 is not mounted and there is no intelligence-system
            // fallback to call _ensureSession.
            const activeTabId = getActiveTabId() ?? "";
            const execStore = usePluginExecutionStore.getState();
            const hasSession =
                execStore.sessionId ||
                tabSessionRegistry.getSessionForTab(activeTabId);
            const hasInflight = tabSessionCreationController.has(activeTabId);
            if (!hasSession && !hasInflight && !autoSessionTimer) {
                const capturedTabId = activeTabId;
                autoSessionTimer = setTimeout(() => {
                    autoSessionTimer = null;
                    if (!tabHasExecutableSyntax(capturedTabId)) return;
                    usePluginExecutionStore
                        .getState()
                        ._ensureSession()
                        .catch((err) => {
                            console.warn(
                                "[magic-md] Auto-session (parsedDocument) failed:",
                                err?.message ?? err,
                            );
                        });
                }, 0);
            }
        }

        // Restore outputs from ::import output fences ("import = run").
        // These persist imported definition results directly in the markdown.
        if (parsedDocument?.imports) {
            const current = usePluginExecutionStore.getState().executions;
            const next = new Map(current);
            let changed = false;
            for (const imp of parsedDocument.imports) {
                const cached = imp.output;
                if (!cached) continue;

                const restoredOutputs = new Map<string, PluginOutput>();

                for (const sym of cached.symbols ?? []) {
                    if (sym.kind !== "node") continue;
                    const data = getImportCachedSymbolOutput(imp.namespace, cached, sym);
                    if (data) {
                        restoredOutputs.set(sym.qualifiedId, data as PluginOutput);
                    }
                }

                // Also restore instanceSymbols (re-exported / internal instances)
                for (const sym of cached.instanceSymbols ?? []) {
                    if (sym.kind !== "node") continue;
                    const data = getImportCachedSymbolOutput(imp.namespace, cached, sym);
                    if (data) {
                        restoredOutputs.set(sym.qualifiedId, data as PluginOutput);
                    }
                }

                for (const [unitId, data] of restoredOutputs) {
                    if (current.has(unitId)) continue;
                    const hasError = !!(data as Record<string, unknown>)?.__error;
                    const meta = data?.__metadata as Record<string, unknown> | undefined;
                    next.set(unitId, {
                        unitId,
                        status: hasError ? "error" : "success",
                        actionName: (meta?.actionName as string) ?? null,
                        result: hasError
                            ? {
                                success: false,
                                message: (
                                    (data as Record<string, unknown>).__error as {
                                        message: string;
                                    }
                                ).message,
                            }
                            : { success: true, output: data },
                        output: data,
                        startedAt: null,
                        duration: (meta?.duration as number) ?? null,
                    });
                    changed = true;
                }
            }
            if (changed) usePluginExecutionStore.setState({ executions: next });
        }
    }
});

/* -- Session sync: when content changes, sync to Engine Server -- */

useEditorStore.subscribe((state, prev) => {
    if (state.tabs === prev.tabs) {
        return;
    }

    // Check all tabs that have sessions for content changes
    for (const tab of state.tabs) {
        const tabId = tab.id;
        const content = tab.content;

        // Skip tabs that are still loading from disk — their content is a
        // placeholder and must NOT be synced to the engine session.
        if (tab.isLoading) continue;

        if (content === tabPrevContentMap.get(tabId)) continue;
        tabPrevContentMap.set(tabId, content);

        // Clear execution error when active document is edited
        if (tabId === getActiveTabId()) {
            usePluginExecutionStore.setState({ executionError: null });
        }

        // Resolve session for this tab: ONLY use the registry.
        // The global sessionId fallback was removed because it could
        // route a stale global session to the wrong tab, causing content
        // to be synced to a session that belongs to a different document.
        const tabSessionId = tabSessionRegistry.getSessionForTab(tabId) ?? null;
        if (!tabSessionId) continue;

        // Debounce session sync to avoid excessive RPC calls during typing
        const prevTimer = tabSyncTimerMap.get(tabId);
        if (prevTimer) clearTimeout(prevTimer);

        const timer = setTimeout(async () => {
            try {
                await syncTabSessionIfNeeded(tabId, tabSessionId);
            } catch {
                // Session may have been closed; ignore
            } finally {
                if (tabSyncTimerMap.get(tabId) === timer) {
                    tabSyncTimerMap.delete(tabId);
                }
            }
        }, 300);
        tabSyncTimerMap.set(tabId, timer);
    }
});

// ── Old session lifecycle subscriber removed ──
// Session & execution management on tab switch is now handled by the
// subscriber registered before "auto-restore from parsed output" above.

// ── Bridge: keep the store's `tabSessionMap` in sync with the registry ──
// The registry is the source of truth; this subscription mirrors changes
// into the store so existing reactive selectors continue to fire.
tabSessionRegistry.subscribe(() => {
    usePluginExecutionStore.setState({
        tabSessionMap: tabSessionRegistry.snapshotTabSessionMap(),
    });
});

// ── Wire active-tab provider (P2C #4) ──
// `editor-store.activeTabId` is the single source of truth.  Register the
// default provider at module load so all `getActiveTabId()` consumers above
// resolve through editor-store without holding a duplicated copy.  Tests
// can override this by calling `setActiveTabIdProvider(...)`.
setActiveTabIdProvider(() => {
    const id = useEditorStore.getState().activeTabId;
    return id === "" ? undefined : id;
});

setEngineInitializer(() =>
    usePluginExecutionStore.getState()._initEngine(),
);

/**
 * Explicit adapter from the execution store to notification projection logic.
 * notification-handlers imports this module only for types, so runtime module
 * initialization remains one-way: store -> handlers.
 */
export const pluginExecutionNotificationHandlerDependencies = {
    _syncBackgroundRunningTabs,
    getActiveTabId,
    reconcileEngineTabContent,
    resyncExternalConfigFile,
    resyncExecutionSessionProjection,
};
